<template>
  <div class="test-system" :class="{ 'test-active': testStarted }">
    <!-- LIMITED_STUDENT Access Denied Screen -->
    <v-container v-if="isLimitedStudent" class="py-12">
      <v-card class="pa-8 text-center restriction-card mx-auto" elevation="12" max-width="700">
        <v-icon icon="mdi-shield-alert" size="80" color="warning" class="mb-4" />
        <h2 class="text-h4 mb-4 text-warning font-weight-bold">{{ $t('student.restrictions.title') }}</h2>
        <v-alert
          color="info"
          variant="tonal"
          class="mb-4 text-start"
        >
          <v-icon icon="mdi-information" class="me-2" />
          {{ $t('student.restrictions.message') }}
        </v-alert>
        
        <v-card class="pa-4 mb-4" color="grey-lighten-4" variant="tonal">
          <h4 class="mb-2 text-primary">{{ $t('student.restrictions.availableFeatures') }}</h4>
          <v-list density="compact" class="bg-transparent">
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-check" color="success" />
              </template>
              <v-list-item-title>{{ $t('student.restrictions.chatAccess') }}</v-list-item-title>
            </v-list-item>
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-check" color="success" />
              </template>
              <v-list-item-title>{{ $t('student.restrictions.profileAccess') }}</v-list-item-title>
            </v-list-item>
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-close" color="error" />
              </template>
              <v-list-item-title>{{ $t('student.restrictions.testAccess') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
        
        <v-divider class="my-4" />
        <p class="text-body-2 text-medium-emphasis mb-4">
          <v-icon icon="mdi-phone" class="me-1" size="small" />
          {{ $t('student.restrictions.adminContact') }}
        </p>
        <v-btn
          color="primary"
          variant="elevated"
          @click="router.push('/student/dashboard')"
          size="large"
          class="me-2"
        >
          <v-icon start>mdi-arrow-left</v-icon>
          {{ $t('common.backToDashboard') }}
        </v-btn>
        <v-btn
          color="success"
          variant="outlined"
          href="tel:+7-XXX-XXX-XX-XX"
          size="large"
        >
          <v-icon start>mdi-phone</v-icon>
          {{ $t('student.restrictions.callAdmin') }}
        </v-btn>
      </v-card>
    </v-container>

    <!-- Auto Starting Screen -->
    <v-container v-else-if="autoStarting" class="py-12">
      <v-card class="pa-8 text-center mode-selection-card" elevation="8">
        <v-progress-circular 
          indeterminate 
          color="primary" 
          size="64" 
          width="6"
          class="mb-4"
        />
        <h2 class="text-h5 mb-2">{{ $t('test.preparing') }}</h2>
        <p class="text-body-1 text-medium-emphasis">{{ $t('test.pleaseWait') }}</p>
      </v-card>
    </v-container>

    <!-- Mode Selection Screen -->
    <v-container v-else-if="!testStarted && !showResults && !showLessons" class="mode-selection-container">
      <v-card class="pa-6 mode-selection-card mx-auto" elevation="8" max-width="900">
        <h1 class="text-center text-gradient mb-6 mode-selection-title">
          {{ $t('test.title') }}
        </h1>

        <!-- Mode Switch Buttons -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-btn-toggle
              v-model="selectedMode"
              mandatory
              color="primary"
              class="d-flex"
              variant="outlined"
            >
              <v-btn value="TRAINING_MODE" class="flex-grow-1">
                <v-icon start>mdi-school</v-icon>
                {{ $t('test.modes.training') }}
              </v-btn>
              <v-btn value="ONLINE_LESSONS" class="flex-grow-1" @click="startOnlineLessons">
                <v-icon start>mdi-video-box</v-icon>
                {{ $t('test.modes.onlineLessons') }}
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <v-row class="mb-4">
          <v-col cols="12">
            <div class="training-submode-label">
              {{ $t('test.trainingVariant') }}
            </div>
            <v-btn-toggle
              v-model="trainingVariant"
              mandatory
              color="primary"
              class="d-flex training-submode-toggle"
              variant="outlined"
            >
              <v-btn value="standard" class="flex-grow-1">
                <v-icon start>mdi-school-outline</v-icon>
                {{ $t('test.standardTraining') }}
              </v-btn>
              <v-btn value="mistakes" class="flex-grow-1">
                <v-icon start>mdi-refresh-auto</v-icon>
                {{ mistakeReviewToggleLabel }}
              </v-btn>
              <v-btn
                v-if="assignedTestsLoading || hasAssignedTests"
                value="assigned"
                class="flex-grow-1"
              >
                <v-icon start>mdi-clipboard-text-play-outline</v-icon>
                {{ assignedTrainingToggleLabel }}
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
        
        <!-- Unified Training/Exam Mode Card -->
        <v-card
          v-if="!compactMistakeModeCard"
          class="training-card pa-5 mb-5"
          elevation="4"
          color="primary"
          variant="tonal"
        >
          <!-- Exam Mode Toggle at the top -->
          <v-row v-if="!isMistakeReviewMode && !isAssignedTestMode" align="center" class="mb-4">
            <v-col cols="12" class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon icon="mdi-certificate" size="20" color="warning" class="mr-2" />
                <div>
                  <h4 class="text-body-1 font-weight-bold text-white">{{ $t('test.enableExamMode') }}</h4>
                  <p class="text-caption text-grey-lighten-2 mb-0">{{ $t('test.examModeDesc') }}</p>
                </div>
              </div>
              <v-switch
                v-model="isExamMode"
                color="warning"
                hide-details
                inset
                density="compact"
              />
            </v-col>
          </v-row>

          <!-- Main content -->
          <div
            class="text-center"
            :class="{ 'compact-mode-card-content': compactMistakeModeCard }"
          >
            <v-icon
              :icon="modeCardState.icon"
              :size="compactMistakeModeCard ? 34 : 56"
              :color="modeCardState.color"
              :class="compactMistakeModeCard ? 'compact-mode-card-icon' : 'mb-2'"
            />
            <h2
              class="text-h5 text-white"
              :class="compactMistakeModeCard ? 'compact-mode-card-title mb-0' : 'mb-2'"
            >
              {{ modeCardState.title }}
            </h2>
            <p v-if="!compactMistakeModeCard" class="text-body-2 text-grey-lighten-1">
              {{ modeCardState.description }}
            </p>
          </div>
        </v-card>
        
        <!-- Topics Selection (Multiple) - СЌРєР·Р°РјРµРЅ СЂРµР¶РёРјС–РЅРґРµ Р¶Р°СЃС‹СЂС‹РЅ -->
        <v-card
          v-if="isMistakeReviewMode"
          class="mistake-review-card pa-4 mb-5"
          elevation="0"
          color="warning"
          variant="tonal"
        >
          <div
            class="d-flex align-start justify-space-between flex-wrap ga-3 mb-3"
            :class="{ 'compact-mistake-review-header mb-2': isMobile }"
          >
            <div>
              <div v-if="!isMobile" class="text-overline mistake-review-kicker">
                {{ $t('test.mistakeReview') }}
              </div>
              <div class="text-h6 text-white" :class="{ 'compact-mistake-review-title': isMobile }">
                {{ $t('test.mistakeReviewAvailable', { count: incorrectQuestionsCount }) }}
              </div>
              <p v-if="!isMobile" class="text-body-2 text-grey-lighten-1 mb-1">
                {{ $t('test.mistakeReviewOnlyWrong') }}
              </p>
              <p v-if="!isMobile" class="text-body-2 text-grey-lighten-1 mb-0">
                {{ $t('test.mistakeReviewSavedToStats') }}
              </p>
              <p
                v-else
                class="text-body-2 text-grey-lighten-1 mb-0 compact-mistake-review-text"
              >
                {{ $t('test.mistakeReviewCompactHint') }}
              </p>
            </div>

            <v-chip
              :color="incorrectQuestionsCount > 0 ? 'warning' : 'success'"
              variant="flat"
              size="large"
              class="font-weight-bold"
            >
              {{ incorrectQuestionsCount }}
            </v-chip>
          </div>

          <v-progress-linear
            v-if="incorrectQuestionsLoading"
            indeterminate
            color="warning"
            rounded
            class="mb-2"
          />

          <template v-else-if="incorrectQuestionsCount > 0">
            <div
              class="mistake-count-section"
              :class="{ 'mistake-count-section-mobile': isMobile }"
            >
              <div
                v-if="!isMobile"
                class="text-caption text-grey-lighten-1 mb-2"
              >
                {{ mistakeReviewCountHeading }}
              </div>

              <v-btn-toggle
                v-model="mistakeReviewCountPreset"
                mandatory
                color="warning"
                class="d-flex mistake-count-toggle mb-2"
                :class="{ 'mistake-count-toggle-mobile mb-0': isMobile }"
                variant="outlined"
              >
                <v-btn
                  v-for="option in mistakeReviewCountOptions"
                  :key="option.value"
                  :value="option.value"
                  class="flex-grow-1"
                >
                  {{ option.label }}
                </v-btn>
              </v-btn-toggle>
            </div>

            <div v-if="!isMobile" class="text-caption text-grey-lighten-1">
              {{ $t('test.mistakeReviewCountHint', { count: resolvedMistakeQuestionCount }) }}
            </div>
          </template>

          <v-alert
            v-else
            type="success"
            variant="tonal"
            density="comfortable"
            class="mt-2"
          >
            {{ $t('test.mistakeReviewNoQuestions') }}
          </v-alert>
        </v-card>

        <v-card
          v-if="isAssignedTestMode"
          class="assigned-test-card pa-4 mb-5"
          elevation="0"
          color="info"
          variant="tonal"
        >
          <div class="d-flex align-start justify-space-between flex-wrap ga-3 mb-3">
            <div>
              <div class="text-overline assigned-test-kicker">
                {{ $t('test.assignedTests') }}
              </div>
              <div class="text-h6 text-white">
                {{ currentAssignedTest ? currentAssignedTest.title : $t('test.assignedTests') }}
              </div>
              <p class="text-body-2 text-grey-lighten-1 mb-0">
                {{ $t('test.assignedTestsHint') }}
              </p>
            </div>

            <v-chip
              color="info"
              variant="flat"
              size="large"
              class="font-weight-bold"
            >
              {{ assignedTests.length }}
            </v-chip>
          </div>

          <v-progress-linear
            v-if="assignedTestsLoading"
            indeterminate
            color="info"
            rounded
            class="mb-3"
          />

          <template v-else-if="hasAssignedTests">
            <v-select
              v-model="selectedAssignedTestId"
              :items="assignedTestOptions"
              item-title="title"
              item-value="value"
              :label="$t('test.selectAssignedTest')"
              variant="outlined"
              density="comfortable"
              hide-details
              class="mb-3"
            />

            <div v-if="currentAssignedTest" class="d-flex flex-wrap ga-2 mb-2">
              <v-chip size="small" color="info" variant="tonal">
                {{ $t('test.questionCount') }}: {{ currentAssignedTest.questionCount }}
              </v-chip>
              <v-chip size="small" variant="tonal">
                {{ $t('adminAnalytics.lastActivity') }}: {{ formatAssignmentDate(currentAssignedTest.createdAt) }}
              </v-chip>
            </div>

            <div
              v-if="currentAssignedTest?.notes"
              class="text-body-2 text-grey-lighten-1"
            >
              {{ currentAssignedTest.notes }}
            </div>
          </template>

          <v-alert
            v-else
            type="info"
            variant="tonal"
            density="comfortable"
            class="mt-2"
          >
            {{ $t('test.noAssignedTests') }}
          </v-alert>
        </v-card>

        <v-row class="mb-3" v-if="showTopicsSelector">
          <v-col cols="12">
            <v-select
              v-model="selectedTopics"
              :items="topicCategories"
              :label="$t('test.selectTopics')"
              :loading="topicsLoading"
              :disabled="topicsLoading"
              variant="outlined"
              density="comfortable"
              multiple
              chips
              closable-chips
              :hint="showTopicsHint ? $t('test.topicsHint') : ''"
              :persistent-hint="showTopicsHint"
            >
              <template #prepend-inner>
                <v-icon icon="mdi-folder-multiple" />
              </template>
            </v-select>
          </v-col>
        </v-row>
        
        <!-- Р­РєР·Р°РјРµРЅ СЂРµР¶РёРјС–РЅРґРµ Р±Р°СЂР»С‹Т› С‚Р°Т›С‹СЂС‹РїС‚Р°РЅ С…Р°Р±Р°СЂР»Р°РјР° -->
        <v-row class="mb-3" v-if="showExamTopicsInfo">
          <v-col cols="12">
            <v-alert type="info" variant="tonal" density="compact">
              <v-icon start>mdi-information</v-icon>
              {{ $t('test.examAllTopics') }}
            </v-alert>
          </v-col>
        </v-row>
        
        <!-- Question Count -->
        <v-row v-if="!isMistakeReviewMode && !isAssignedTestMode" class="mb-5">
          <v-col cols="12">
            <v-text-field
              v-model.number="questionCount"
              type="number"
              :label="$t('test.questionCount')"
              variant="outlined"
              density="comfortable"
              min="5"
              max="40"
              :disabled="isExamMode"
              :readonly="isExamMode"
              hide-details
            >
              <template #prepend-inner>
                <v-icon icon="mdi-numeric" />
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        
        <!-- Start Button -->
        <div
          class="mode-selection-start-bar"
          :class="{ 'mode-selection-start-bar-inline': inlineStartBarOnMobile }"
        >
          <v-btn
            color="primary"
            size="x-large"
            block
            class="mode-selection-start-btn"
            :disabled="!canStartConfiguredMode"
            @click="startTest(true)"
            :loading="loading"
          >
            <v-icon start>{{ startButtonIcon }}</v-icon>
            {{ startButtonLabel }}
          </v-btn>
        </div>
      </v-card>
    </v-container>

    <!-- Test Interface -->
    <div 
      v-if="testStarted && !showResults" 
      class="test-interface fullscreen"
      ref="testInterfaceRef"
    >
      <!-- Top Bar -->
      <div ref="testTopbarRef" class="test-topbar-container">
        <!-- Mobile Top Actions Bar (above question indicators) -->
        <div v-if="isMobile" class="mobile-top-actions">
          <!-- Language Buttons - Direct Toggle -->
          <div class="mobile-lang-buttons">
            <v-btn
              v-for="lang in availableLanguages"
              :key="lang.code"
              @click="changeLanguage(lang.code)"
              :color="selectedLanguage === lang.code ? 'primary' : 'grey'"
              :variant="selectedLanguage === lang.code ? 'flat' : 'text'"
              size="x-small"
              class="mobile-lang-toggle"
              density="compact"
            >
              {{ lang.shortName }}
            </v-btn>
          </div>
          
          <!-- Timer (for exam mode) - Centered -->
          <div v-if="selectedMode === 'EXAM'" class="mobile-timer">
            <v-chip
              :color="timeLeft < 300 ? 'error' : 'warning'"
              variant="flat"
              size="x-small"
              density="compact"
            >
              <v-icon icon="mdi-clock-outline" size="x-small" class="me-1" />
              <span class="text-caption font-weight-bold">{{ formatTime(timeLeft) }}</span>
            </v-chip>
          </div>

          <!-- Theme Switcher -->
          <ThemeSwitcher
            class="mobile-theme-switcher"
            variant="text"
            size="x-small"
          />

          <!-- Close Button -->
          <v-btn
            icon="mdi-close"
            @click="requestCloseTest"
            color="error"
            variant="flat"
            size="x-small"
            class="mobile-close-btn"
          />
        </div>
        
        <div class="test-toolbar-content">
          <!-- Left section: Question Indicators -->
          <div class="toolbar-left-section">
            <div class="question-indicators" ref="questionIndicatorsRef">
              <div
                v-for="(test, index) in tests"
                :key="index"
                class="question-indicator"
                :class="{
                  'correct': questionStates[index] === 'correct',
                  'wrong': questionStates[index] === 'wrong',
                  'skipped': questionStates[index] === 'skipped',
                  'answered': isExamMode && questionStates[index] === 'answered',
                  'current': currentQuestionIndex === index
                }"
                @click="goToQuestion(index)"
                :title="`${$t('test.question')} ${index + 1}`"
              >
                {{ index + 1 }}
              </div>
            </div>
          </div>
          
          <!-- Desktop right section with language selector -->
          <div class="toolbar-right-section-desktop" v-if="!isMobile">
            <!-- Desktop Language Buttons -->
            <div class="desktop-lang-buttons">
              <v-btn
                v-for="lang in ['kz', 'ru', 'en']"
                :key="lang"
                :variant="locale === lang ? 'flat' : 'outlined'"
                :color="locale === lang ? 'primary' : 'default'"
                size="small"
                class="mx-1"
                @click="changeLanguage(lang)"
              >
                {{ lang.toUpperCase() }}
              </v-btn>
            </div>
            
            <!-- Timer (for exam mode) -->
            <div v-if="selectedMode === 'EXAM' || isExamMode" class="timer ml-3">
              <v-icon icon="mdi-clock-outline" class="me-2" />
              <span class="text-h6" :class="timeLeft < 300 ? 'text-error' : ''">
                {{ formatTime(timeLeft) }}
              </span>
            </div>

            <!-- Theme Switcher -->
            <ThemeSwitcher
              class="ml-3"
              variant="outlined"
              size="small"
            />

            <!-- Close Button -->
            <v-btn
              icon="mdi-close"
              class="close-test-btn-toolbar ml-3"
              @click="requestCloseTest"
              color="error"
              variant="elevated"
              size="default"
            />
          </div>
        </div>
      </div>

      <!-- Mobile Floating Controls - REMOVED, now using mobile-top-actions -->

      <!-- Main Content -->
      <v-container fluid class="test-content" v-if="currentTest">
        <v-row no-gutters class="test-row">
          <!-- Video Section (Left) -->
          <v-col cols="12" md="7" lg="7" class="video-section">
            <div ref="videoSectionRef" class="video-container" @contextmenu.prevent>
              <LogoMedicalAnimation
                v-if="currentTest && (!resolvedCurrentVideo || videoError) && currentTest.video1 === '__LOGO_MEDICAL__'"
                class="test-video"
              />
              <NoVideoAnimation
                v-else-if="currentTest && (!resolvedCurrentVideo || videoError)"
                class="test-video"
              />
              <video
                v-else-if="currentTest"
                ref="videoPlayer"
                :key="resolvedCurrentVideo"
                muted
                autoplay
                playsinline
                webkit-playsinline
                preload="auto"
                controlslist="nodownload"
                disablePictureInPicture
                class="test-video"
                @loadeddata="onVideoReady"
                @loadedmetadata="onVideoReady"
                @ended="onVideoEnded"
                @error="onVideoError"
                @click="togglePlayPause"
                @contextmenu.prevent
              >
                <source :src="resolvedCurrentVideo" type="video/mp4" />
              </video>

              <!-- Custom Video Restart Button -->
              <div v-if="currentTest && resolvedCurrentVideo && !videoError" class="video-restart-button" @click="restartVideo">
                <v-btn
                  icon
                  color="amber"
                  size="large"
                  class="restart-btn"
                >
                  <v-icon size="28">mdi-restart</v-icon>
                </v-btn>
              </div>

              <!-- Video Loading -->
              <div v-if="videoLoading && resolvedCurrentVideo && !videoError" class="video-loading">
                <v-progress-circular indeterminate color="primary" size="64" />
              </div>
            </div>
          </v-col>

          <!-- Question Section (Right) -->
          <v-col cols="12" md="5" lg="5" class="question-section pa-6">
            <div v-if="currentTest" class="question-content">
              <div
                v-if="activeAssignedTestMeta?.title || activeTestVersionLabel"
                class="question-meta-row"
              >
                <v-chip
                  v-if="activeAssignedTestMeta?.title"
                  color="info"
                  variant="tonal"
                  size="small"
                  class="question-meta-chip"
                >
                  <v-icon start size="small">mdi-clipboard-text-outline</v-icon>
                  {{ activeAssignedTestMeta.title }}
                </v-chip>
                <v-chip
                  v-if="activeTestVersionLabel"
                  color="primary"
                  variant="tonal"
                  size="small"
                  class="question-meta-chip question-version-chip"
                >
                  <v-icon start size="small">mdi-source-branch</v-icon>
                  {{ activeTestVersionLabel }}
                </v-chip>
              </div>

              <!-- Question Text -->
              <h2 ref="questionHeadingRef" class="text-h5 mb-4 question-text" tabindex="-1">
                {{ getQuestionText(currentTest) }}
              </h2>

              <!-- Answer Options -->
              <div class="answer-options" role="list">
                <button
                  v-for="answerEntry in getAnswerEntries(currentTest)"
                  :key="`answer-${currentQuestionIndex}-${answerEntry.actualIndex}`"
                  type="button"
                  class="answer-card mb-3 pa-4"
                  :class="getAnswerClass(answerEntry.actualIndex)"
                  :aria-pressed="userAnswer === answerEntry.actualIndex"
                  @pointerdown.stop="handleAnswerPointerDown(answerEntry.actualIndex, $event)"
                  @pointerup.stop="handleAnswerPointerUp(answerEntry.actualIndex, $event)"
                  @pointercancel="clearAnswerPointerState"
                  @click.stop="handleAnswerClick(answerEntry.actualIndex)"
                >
                  <div class="d-flex align-center">
                    <span
                      class="answer-option-indicator me-3"
                      :class="{
                        'is-selected': userAnswer === answerEntry.actualIndex,
                        'is-correct': showCorrectAnswer && answerEntry.actualIndex === currentTest.correctAnswer,
                        'is-incorrect': showCorrectAnswer && userAnswer === answerEntry.actualIndex && answerEntry.actualIndex !== currentTest.correctAnswer
                      }"
                      aria-hidden="true"
                    >
                      <span class="answer-option-dot"></span>
                    </span>
                    <span class="text-body-1 flex-grow-1">{{ answerEntry.text }}</span>
                    <v-icon
                      v-if="showCorrectAnswer && answerEntry.actualIndex === currentTest.correctAnswer"
                      icon="mdi-check-circle"
                      color="success"
                      class="ms-2"
                    />
                    <v-icon
                      v-if="showCorrectAnswer && userAnswer === answerEntry.actualIndex && answerEntry.actualIndex !== currentTest.correctAnswer"
                      icon="mdi-close-circle"
                      color="error"
                      class="ms-2"
                    />
                  </div>
                </button>
              </div>

              <!-- Explanation (shown after answer in learning modes, NOT in exam) -->
              <v-expand-transition>
                <v-alert
                  v-if="showExplanation && !isExamMode"
                  ref="explanationRef"
                  type="info"
                  variant="tonal"
                  class="mt-4"
                  tabindex="-1"
                  prominent
                >
                  <div class="text-subtitle-2 mb-2">{{ $t('test.explanation') }}:</div>
                  {{ getExplanation(currentTest) }}
                </v-alert>
              </v-expand-transition>

              <!-- Action Buttons -->
              <div class="mt-6 d-flex gap-3 action-buttons-container">
                <!-- Р­РєР·Р°РјРµРЅ СЂРµР¶РёРјС–РЅРґРµ Р¶Р°СѓР°Рї С‚Р°ТЈРґР°Р»Т“Р°РЅ Р±РѕР»СЃР° Р±Р°С‚С‹СЂРјР° РєУ©СЂС–РЅРµРґС– -->
                <template v-if="answered || (isExamMode && userAnswer !== null)">
                  <v-btn
                    v-if="currentQuestionIndex < (tests?.length || 0) - 1"
                    ref="nextButtonRef"
                    color="primary"
                    size="large"
                    @click="isExamMode ? nextQuestionExam() : nextQuestion()"
                    block
                  >
                    {{ $t('test.nextQuestion') }}
                    <v-icon end>mdi-arrow-right</v-icon>
                  </v-btn>
                  
                  <v-btn
                    v-else
                    ref="nextButtonRef"
                    color="success"
                    size="large"
                    @click="isExamMode ? finishExam() : finishTest()"
                    block
                  >
                    {{ $t('test.finishTest') }}
                    <v-icon end>mdi-check</v-icon>
                  </v-btn>
                </template>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>

  <v-dialog v-model="showCloseConfirm" max-width="420" :z-index="12000">
      <v-card>
        <v-card-title class="text-h6">
          {{ $t('test.exitConfirmTitle') }}
        </v-card-title>
        <v-card-text class="text-body-1">
          {{ $t('test.exitConfirmMessage') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="tonal" color="primary" @click="cancelCloseTest">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="error" @click="confirmCloseTest">
            {{ $t('test.exitConfirmAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Training Mode Finish Dialog -->
    <v-dialog 
      v-model="showTrainingFinishDialog" 
      max-width="600" 
      :z-index="12000"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title class="text-h5 text-center pa-6 bg-primary">
          <v-icon icon="mdi-school" color="white" class="me-2" size="large" />
          <span class="text-white">{{ $t('test.trainingComplete') }}</span>
        </v-card-title>
        <v-card-text class="text-center py-6">
          <p class="text-h6 mb-6">{{ $t('test.trainingCompleteMessage') }}</p>
          <v-row class="mb-4">
            <v-col cols="6" sm="3">
              <v-card class="pa-3" color="primary" variant="tonal">
                <div class="text-h5 text-primary">{{ pendingResults?.score || 0 }}%</div>
                <div class="text-caption">{{ $t('test.score') }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card class="pa-3" color="success" variant="tonal">
                <div class="text-h5 text-success">{{ pendingResults?.correctAnswers || 0 }}</div>
                <div class="text-caption">{{ $t('test.correctAnswers') }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card class="pa-3" color="error" variant="tonal">
                <div class="text-h5 text-error">{{ pendingResults?.wrongAnswers || 0 }}</div>
                <div class="text-caption">{{ $t('test.wrongAnswers') }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card class="pa-3" color="warning" variant="tonal">
                <div class="text-h5 text-warning">{{ pendingResults?.skippedQuestions || 0 }}</div>
                <div class="text-caption">{{ $t('test.skipped') }}</div>
              </v-card>
            </v-col>
          </v-row>
          <v-row class="mb-4">
            <v-col cols="12">
              <v-card class="pa-3" color="info" variant="tonal">
                <div class="text-h6 text-info">
                  {{ pendingResults?.answeredQuestions || 0 }}/{{ pendingResults?.totalQuestions || 0 }} {{ $t('test.answered') }}
                </div>
              </v-card>
            </v-col>
          </v-row>
          <v-divider class="my-4"></v-divider>
          <v-chip color="success" variant="elevated" class="mb-4">
            <v-icon start>mdi-check-circle</v-icon>
            {{ $t('test.resultsSaved') }}
          </v-chip>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-container fluid class="pa-0">
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-btn
                  color="primary"
                  variant="elevated"
                  block
                  size="x-large"
                  @click="restartTraining"
                  height="56"
                >
                  <v-icon start size="large">mdi-refresh</v-icon>
                  <span class="font-weight-bold text-wrap">{{ $t('test.restartTraining') }}</span>
                </v-btn>
              </v-col>
              <v-col cols="12" sm="6">
                <v-btn
                  color="primary"
                  variant="outlined"
                  block
                  size="x-large"
                  @click="returnToTestSystem"
                  height="56"
                >
                  <v-icon start size="large">mdi-view-dashboard-outline</v-icon>
                  <span class="font-weight-bold text-wrap">{{ $t('test.backToTestSystem') }}</span>
                </v-btn>
              </v-col>
              <v-col cols="12">
                <v-btn
                  variant="outlined"
                  block
                  size="large"
                  @click="closeTrainingDialog"
                  height="48"
                >
                  <v-icon start>mdi-close</v-icon>
                  {{ $t('common.close') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- All Tests Completed Dialog - Р‘Р°СЂР»С‹Т› С‚РµСЃС‚С‚РµСЂ Р°СЏТ›С‚Р°Р»РґС‹ -->
    <v-dialog 
      v-model="showAllTestsCompletedDialog" 
      max-width="600" 
      :z-index="12000"
      persistent
    >
      <v-card>
        <v-card-title class="text-h5 text-center pa-6 bg-success">
          <v-icon icon="mdi-trophy" color="white" class="me-2" size="large" />
          <span class="text-white">{{ $t('test.allCompleted') }}</span>
        </v-card-title>
        <v-card-text class="text-center py-8">
          <v-icon icon="mdi-check-decagram" color="success" size="80" class="mb-4" />
          <h2 class="text-h5 mb-4">{{ $t('test.allTestsCompletedTitle') }}</h2>
          <p class="text-body-1 text-medium-emphasis mb-4">
            {{ $t('test.allTestsCompletedMessage') }}
          </p>
          <v-alert type="info" variant="tonal" class="text-start mb-4">
            <v-icon icon="mdi-information" class="me-2" />
            {{ $t('test.resetProgressInfo') }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-container fluid class="pa-0">
            <v-row dense>
              <v-col cols="12">
                <v-btn
                  color="success"
                  variant="elevated"
                  block
                  size="x-large"
                  @click="resetTestProgress"
                  :loading="resetProgressLoading"
                  class="mb-3"
                  height="56"
                >
                  <v-icon start size="large">mdi-refresh</v-icon>
                  <span class="text-h6">{{ $t('test.resetAndRestart') }}</span>
                </v-btn>
              </v-col>
              <v-col cols="12">
                <v-btn
                  color="grey-darken-1"
                  variant="outlined"
                  block
                  size="large"
                  @click="closeAllTestsCompletedDialog"
                  height="48"
                >
                  <v-icon start>mdi-view-dashboard</v-icon>
                  {{ $t('test.backToMenu') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Online Lessons Interface -->
    <v-container v-if="showLessons" class="py-8">
      <!-- Lesson Categories Selection -->
      <v-card v-if="!selectedLessonCategory" class="pa-6 mx-auto" elevation="8" max-width="1200">
        <div class="d-flex align-center mb-6">
          <v-btn
            icon="mdi-arrow-left"
            variant="text"
            @click="backToModeSelection"
          />
          <h1 class="text-h5 text-gradient ml-4">
            {{ $t('lessons.title') }}
          </h1>
        </div>

        <v-progress-linear v-if="lessonsLoading" indeterminate color="primary" class="mb-4" />

        <!-- Categories as List -->
        <v-list v-if="!lessonsLoading" class="pa-0">
          <v-list-item
            v-for="(category, index) in lessonCategories"
            :key="category.id"
            class="lesson-category-item mb-2"
            @click="selectLessonCategory(category.id)"
            border
            rounded
          >
            <v-list-item-title class="text-body-2 mb-1 d-flex align-center">
              <span class="lesson-number-white mr-2">{{ index + 1 }}.</span>
              <span>{{ category[`title${locale.charAt(0).toUpperCase() + locale.slice(1)}`] || category.titleRu }}</span>
            </v-list-item-title>

            <v-list-item-subtitle v-if="category[`description${locale.charAt(0).toUpperCase() + locale.slice(1)}`]" class="text-caption">
              {{ category[`description${locale.charAt(0).toUpperCase() + locale.slice(1)}`] || category.descriptionRu }}
            </v-list-item-subtitle>

            <template #append>
              <div class="d-flex flex-column align-end">
                <v-chip color="primary" size="small" class="mb-2">
                  {{ category._count?.lessons || 0 }} {{ $t('lessons.lessonsCount') }}
                </v-chip>
                <v-icon color="primary">mdi-chevron-right</v-icon>
              </div>
            </template>
          </v-list-item>
        </v-list>

        <v-alert v-if="!lessonsLoading && lessonCategories.length === 0" type="info" class="mt-4">
          {{ $t('lessons.noCategories') }}
        </v-alert>
      </v-card>

      <!-- Lesson Player -->
      <v-card v-if="selectedLessonCategory && currentLesson" class="lesson-player-card" elevation="8">
        <v-toolbar color="primary" dark>
          <v-btn icon @click="backToCategories">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-toolbar-title>
            {{ currentLesson[`title${locale.charAt(0).toUpperCase() + locale.slice(1)}`] || currentLesson.titleRu }}
          </v-toolbar-title>
        </v-toolbar>

        <v-row no-gutters>
          <!-- Video Player -->
          <v-col cols="12" md="8">
              <div class="lesson-video-container pa-4">
                <div class="lesson-video-wrapper" @contextmenu.prevent>
                  <video
                    v-if="resolvedLessonVideoUrl"
                    ref="lessonVideoPlayer"
                    :key="resolvedLessonVideoUrl"
                    controls
                    playsinline
                    webkit-playsinline
                    preload="metadata"
                    controlslist="nodownload"
                    disablePictureInPicture
                    class="lesson-video"
                    @error="onLessonVideoError"
                    @contextmenu.prevent
                  >
                    <source :src="resolvedLessonVideoUrl" type="video/mp4" />
                  </video>
                
                <!-- No video available message -->
                <v-alert
                  v-else-if="currentLesson"
                  type="info"
                  variant="tonal"
                  class="ma-4"
                >
                  {{ $t('lessons.noVideo') }}
                </v-alert>
                
                <!-- Transparent overlay to prevent right-click -->
                  <div class="lesson-video-overlay" @contextmenu.prevent></div>
                </div>
              <v-card class="mt-4 pa-4" variant="outlined">
                <h3 class="text-h6 mb-2">
                  {{ currentLesson[`title${locale.charAt(0).toUpperCase() + locale.slice(1)}`] || currentLesson.titleRu }}
                </h3>
                <p class="text-body-2 text-medium-emphasis">
                  {{ currentLesson[`description${locale.charAt(0).toUpperCase() + locale.slice(1)}`] || currentLesson.descriptionRu }}
                </p>
                <v-chip v-if="currentLesson.duration" size="small" class="mt-2">
                  <v-icon start size="small">mdi-clock-outline</v-icon>
                  {{ currentLesson.duration }} {{ $t('lessons.minutes') }}
                </v-chip>
              </v-card>
            </div>
          </v-col>

          <!-- Lesson List -->
          <v-col cols="12" md="4">
            <v-card flat class="lesson-list">
              <v-card-title class="text-h6 pa-4 bg-grey-lighten-4">
                {{ $t('lessons.allLessons') }}
              </v-card-title>
              <v-list>
                <v-list-item
                  v-for="(lesson, index) in currentLessons"
                  :key="lesson.id"
                  :active="currentLesson.id === lesson.id"
                  @click="selectLesson(lesson)"
                  :class="{ 'bg-primary-lighten-5': currentLesson.id === lesson.id }"
                >
                  <template #prepend>
                    <v-icon :color="currentLesson.id === lesson.id ? 'primary' : 'grey'" class="mr-2">
                      mdi-play-circle
                    </v-icon>
                  </template>
                  <v-list-item-title>
                    <strong class="mr-2">{{ index + 1 }}.</strong>
                    {{ lesson[`title${locale.charAt(0).toUpperCase() + locale.slice(1)}`] || lesson.titleRu }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="lesson.duration">
                    <v-icon size="small" class="mr-1">mdi-clock-outline</v-icon>
                    {{ lesson.duration }} {{ $t('lessons.minutes') }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
        </v-row>
      </v-card>
    </v-container>

    <!-- Results Screen -->
    <v-container v-if="showResults" class="py-8">
      <v-card class="pa-6 results-card" elevation="8">
        <div class="text-center mb-6">
          <v-icon 
            :icon="testPassed ? 'mdi-trophy' : 'mdi-restart'" 
            :color="testPassed ? 'success' : 'warning'"
            size="80"
            class="mb-4"
          />
          <h1 class="text-h3 text-gradient mb-2">
            {{ testPassed ? $t('test.passed') : $t('test.failed') }}
          </h1>
          <p class="text-h5 text-medium-emphasis">
            {{ $t('test.results') }}
          </p>
        </div>

        <!-- Stats Grid -->
        <v-row class="mb-6">
          <v-col cols="6" md="3">
            <v-card class="pa-4 text-center stat-card" color="primary" variant="tonal">
              <div class="text-h3 text-primary mb-2">{{ results.score }}%</div>
              <div class="text-body-2">{{ $t('test.score') }}</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card class="pa-4 text-center stat-card" color="success" variant="tonal">
              <div class="text-h3 text-success mb-2">{{ results.correctAnswers }}</div>
              <div class="text-body-2">{{ $t('test.correctAnswers') }}</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card class="pa-4 text-center stat-card" color="error" variant="tonal">
              <div class="text-h3 text-error mb-2">{{ results.wrongAnswers }}</div>
              <div class="text-body-2">{{ $t('test.wrongAnswers') }}</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card class="pa-4 text-center stat-card" color="warning" variant="tonal">
              <div class="text-h3 text-warning mb-2">{{ results.skippedQuestions || 0 }}</div>
              <div class="text-body-2">{{ $t('test.skipped') }}</div>
            </v-card>
          </v-col>
        </v-row>
        
        <!-- Additional Stats Row -->
        <v-row class="mb-6">
          <v-col cols="6" md="4">
            <v-card class="pa-4 text-center stat-card" color="info" variant="tonal">
              <div class="text-h3 text-info mb-2">{{ results.answeredQuestions || userAnswers.length }}/{{ results.totalQuestions }}</div>
              <div class="text-body-2">{{ $t('test.answered') }}</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="4">
            <v-card class="pa-4 text-center stat-card" color="secondary" variant="tonal">
              <div class="text-h3 text-secondary mb-2">{{ formatTime(results.timeSpent) }}</div>
              <div class="text-body-2">{{ $t('test.timeSpent') }}</div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card class="pa-4 text-center stat-card" :color="testPassed ? 'success' : 'error'" variant="tonal">
              <div class="text-h3 mb-2" :class="testPassed ? 'text-success' : 'text-error'">
                {{ testPassed ? '✓' : '✗' }}
              </div>
              <div class="text-body-2">{{ testPassed ? ($t('test.passed')) : ($t('test.failed')) }}</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Р­РєР·Р°РјРµРЅ СЂРµР¶РёРјС–РЅРґРµ Т›Р°С‚Рµ Р¶Р°СѓР°РїС‚Р°СЂ С‚С–Р·С–РјС– -->
        <v-row v-if="(isExamMode || selectedMode === 'TRAINING_MODE') && results.wrongQuestionsList && results.wrongQuestionsList.length > 0" class="mb-6">
          <v-col cols="12">
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-title class="text-error">
                  <v-icon start color="error">mdi-alert-circle</v-icon>
                  {{ $t('test.wrongAnswersList') }} ({{ results.wrongQuestionsList.length }})
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div v-for="(wrongAnswer, idx) in results.wrongQuestionsList" :key="idx" class="wrong-answer-item mb-4">
                    <v-card variant="outlined" class="pa-4">
                      <!-- РЎТ±СЂР°Т› РЅУ©РјС–СЂС– Р¶У™РЅРµ РјУ™С‚С–РЅС– -->
                      <div class="d-flex align-center mb-2">
                        <v-chip color="error" size="small" class="mr-2">{{ wrongAnswer.questionIndex + 1 }}</v-chip>
                        <span class="text-body-1 font-weight-medium">{{ getQuestionText(wrongAnswer.test) }}</span>
                      </div>
                      
                      <!-- Р’РёРґРµРѕ (РµРіРµСЂ Р±Р°СЂ Р±РѕР»СЃР°) -->
                      <div v-if="getVideoUrl(wrongAnswer.test)" class="video-preview mb-3">
                        <video
                          controls
                          playsinline
                          webkit-playsinline
                          class="rounded"
                          style="max-width: 100%; max-height: 200px;"
                          preload="metadata"
                        >
                          <source :src="getVideoUrl(wrongAnswer.test)" type="video/mp4" />
                        </video>
                      </div>
                      
                      <!-- ТљРѕР»РґР°РЅСѓС€С‹РЅС‹ТЈ Р¶Р°СѓР°Р±С‹ -->
                      <v-alert type="error" variant="tonal" density="compact" class="mb-2">
                        <div class="text-caption">{{ $t('test.yourAnswer') }}:</div>
                        <div class="text-body-2">{{ getAnswerByIndex(wrongAnswer.test, wrongAnswer.userAnswer) }}</div>
                      </v-alert>
                      
                      <!-- Р”Т±СЂС‹СЃ Р¶Р°СѓР°Рї -->
                      <v-alert type="success" variant="tonal" density="compact">
                        <div class="text-caption">{{ $t('test.correctAnswer') }}:</div>
                        <div class="text-body-2">{{ getAnswerByIndex(wrongAnswer.test, wrongAnswer.correctAnswer) }}</div>
                      </v-alert>
                      
                      <!-- РўТЇСЃС–РЅРґС–СЂРјРµ -->
                      <v-expand-transition>
                        <v-alert v-if="wrongAnswer.test.explanation" type="info" variant="tonal" density="compact" class="mt-2">
                          <div class="text-caption">{{ $t('test.explanation') }}:</div>
                          <div class="text-body-2">{{ getExplanation(wrongAnswer.test) }}</div>
                        </v-alert>
                      </v-expand-transition>
                    </v-card>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

        <!-- Actions -->
        <v-row>
          <v-col cols="12" md="6">
            <v-btn
              color="primary"
              size="large"
              block
              @click="retryTest"
            >
              <v-icon start>mdi-refresh</v-icon>
              {{ $t('test.tryAgain') }}
            </v-btn>
          </v-col>
          <v-col cols="12" md="6">
            <v-btn
              color="surface"
              size="large"
              block
              @click="goToDashboard"
            >
              <v-icon start>mdi-view-dashboard</v-icon>
              {{ $t('test.backToMenu') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { withBasePath } from '@/utils/path'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useSnackbarStore } from '@/stores/snackbar'
import { useAuthStore } from '@/stores/auth'
import { useSocketStore } from '@/stores/socket'
import { useAssignedTests } from '@/composables/test-system/useAssignedTests'
import { useMistakeReview } from '@/composables/test-system/useMistakeReview'
import { useOnlineLessons } from '@/composables/test-system/useOnlineLessons'
import { useTrainingModeSetup } from '@/composables/test-system/useTrainingModeSetup'
import { buildTestFetchRequest } from '@/composables/test-system/modes/buildTestFetchRequest'
import api from '@/utils/api'
import NoVideoAnimation from '@/components/common/NoVideoAnimation.vue'
import LogoMedicalAnimation from '@/components/common/LogoMedicalAnimation.vue'
import ThemeSwitcher from '@/components/common/ThemeSwitcher.vue'

const { t, locale } = useI18n()
const router = useRouter()
const snackbarStore = useSnackbarStore()
const authStore = useAuthStore()
const socketStore = useSocketStore()

// Refs
const selectedMode = ref('TRAINING_MODE') // Default to training mode
const selectedCategory = ref('all')
const selectedTopics = ref([]) // Multiple topics
const isExamMode = ref(false) // Exam mode switch
const questionCount = ref(40)
const testStarted = ref(false)
const showResults = ref(false)
const loading = ref(false)
const videoLoading = ref(false)
const videoError = ref(false)
const testVideoSrc = ref(null)
const playingVideo2 = ref(false) // video2 (С‚ТЇСЃС–РЅРґС–СЂРјРµ) РѕР№РЅР°Рї Р¶Р°С‚С‹СЂ РјР°
const autoStarting = ref(false)
const isMistakeReviewMode = ref(false)
const isAssignedTestMode = ref(false)
const {
  incorrectQuestionsCount,
  incorrectQuestionsLoading,
  mistakeReviewCountPreset,
  mistakeReviewCountOptions,
  resolvedMistakeQuestionCount,
  loadIncorrectQuestionsSummary,
  decrementIncorrectCount
} = useMistakeReview({
  api,
  t,
  isMistakeReviewMode,
  isAssignedTestMode,
  isExamMode,
  selectedMode,
  selectedTopics
})
const {
  assignedTests,
  assignedTestsLoading,
  selectedAssignedTestId,
  activeAssignedTestId,
  activeAssignedTestMeta,
  hasAssignedTests,
  currentAssignedTest,
  assignedTestOptions,
  clearAssignedTestSession,
  loadAssignedTests
} = useAssignedTests({
  api,
  isAssignedTestMode,
  isMistakeReviewMode,
  isExamMode,
  selectedMode,
  selectedTopics
})
const defaultTestCatalogVersion = typeof __TEST_CATALOG_VERSION__ !== 'undefined'
  ? String(__TEST_CATALOG_VERSION__)
  : (typeof __APP_VERSION__ !== 'undefined' ? String(__APP_VERSION__) : null)
const activeTestVersion = ref(null)
const activeTestVersionLabel = computed(() => {
  const version = activeTestVersion.value || defaultTestCatalogVersion
  return version ? `v${version}` : ''
})

// Topics data
const availableTopics = ref([])
const topicsLoading = ref(false)

// Helper functions for topic formatting
const getTopicNumber = (topic) => {
  if (!topic || typeof topic !== 'string') return null
  const parts = topic.split('|')
  if (parts.length >= 2) {
    const firstPart = parts[0]
    const num = parseInt(firstPart, 10)
    if (!isNaN(num) && num > 0 && num <= 99) {
      return num
    }
  }
  return null
}

const getTopicText = (topic, lang = 'kz') => {
  if (!topic || typeof topic !== 'string') return topic || ''
  const parts = topic.split('|')
  const hasNumber = parts.length >= 2 && !isNaN(parseInt(parts[0], 10)) && parseInt(parts[0], 10) > 0 && parseInt(parts[0], 10) <= 99
  
  if (hasNumber) {
    // Format: "NN|RU|KZ|EN"
    const langIndex = lang === 'ru' ? 1 : lang === 'kz' ? 2 : 3
    return parts[langIndex] || parts[1] || topic
  } else if (parts.length >= 3) {
    // Format: "RU|KZ|EN" (old format without number)
    const langIndex = lang === 'ru' ? 0 : lang === 'kz' ? 1 : 2
    return parts[langIndex] || parts[0] || topic
  }
  return topic
}

const formatTopicDisplay = (topic, lang = 'kz') => {
  const num = getTopicNumber(topic)
  const text = getTopicText(topic, lang)
  if (num) {
    return `${num}. ${text}`
  }
  return text
}

// Language selector
const selectedLanguage = ref(locale.value || 'kz')
const showLanguageMenu = ref(false)
const availableLanguages = ref([
  { code: 'kz', name: 'ТљР°Р·Р°Т›С€Р°', shortName: 'KZ', flag: 'mdi-flag' },
  { code: 'ru', name: 'Р СѓСЃСЃРєРёР№', shortName: 'RU', flag: 'mdi-flag-outline' },
  { code: 'en', name: 'English', shortName: 'EN', flag: 'mdi-flag-variant' }
])

const showCloseConfirm = ref(false)
const showTrainingFinishDialog = ref(false)
const showAllTestsCompletedDialog = ref(false) // Р‘Р°СЂР»С‹Т› С‚РµСЃС‚С‚РµСЂ Р°СЏТ›С‚Р°Р»РґС‹ РґРёР°Р»РѕРіС‹
const resetProgressLoading = ref(false) // РџСЂРѕРіСЂРµСЃСЃС‚С– РЅУ©Р»РґРµСѓ loading state
const pendingResults = ref(null)

const tests = ref([])
const currentQuestionIndex = ref(0)
const userAnswer = ref(null)
const answered = ref(false)
const questionStates = ref({}) // Track question states: correct, wrong, skipped
const userAnswers = ref([])
const startTime = ref(null)
const timeLeft = ref(2400) // 40 minutes in seconds
const timerInterval = ref(null)
const videoPlayer = ref(null)
const videoSectionRef = ref(null)
const submitButtonRef = ref(null)
const nextButtonRef = ref(null)
const explanationRef = ref(null)
const questionHeadingRef = ref(null)

// Swipe functionality refs
const touchStartX = ref(0)
const touchEndX = ref(0)
const touchStartY = ref(0)
const touchEndY = ref(0)
const touchStartedInIndicatorBar = ref(false)
const touchStartedInQuestionArea = ref(false)
const activeAnswerPointer = ref(null)
const activeAnswerPointerStart = ref(null)
const lastTouchAnswerSelectionAt = ref(0)
const testInterfaceRef = ref(null)
const testTopbarRef = ref(null)
const pendingMobileVideoAlign = ref(false)

let savedPageScrollY = 0
let mobileVideoAlignTimeouts = []

// Mobile toolbar visibility
const showMobileControls = ref(false)
const mobileControlsTimeout = ref(null)

const results = ref({
  score: 0,
  correctAnswers: 0,
  wrongAnswers: 0,
  timeSpent: 0
})

const STORAGE_KEY = 'korkem-test-state'
const QUEUE_KEY = 'korkem-test-submit-queue'
const isRestoring = ref(false)

// --- Offline queue: retry submit with exponential backoff ---
const submitWithRetry = async (submissionData, maxRetries = 3) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      await api.post('/tests/submit', submissionData)
      snackbarStore.success(t('test.resultsSaved'))
      return true
    } catch (error) {
      console.error(`Submit attempt ${attempt}/${maxRetries} failed:`, error)
      if (attempt < maxRetries) {
        // Exponential backoff: 1s, 2s, 4s
        await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt - 1)))
      }
    }
  }
  // All retries failed вЂ” save to offline queue
  try {
    const queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]')
    queue.push({ data: submissionData, savedAt: Date.now() })
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue))
    snackbarStore.error(t('test.savedOffline') || 'РќУ™С‚РёР¶РµР»РµСЂ СЃР°Т›С‚Р°Р»РґС‹, РёРЅС‚РµСЂРЅРµС‚ Т›РѕСЃС‹Р»Т“Р°РЅРґР° Р¶С–Р±РµСЂС–Р»РµРґС–')
  } catch (e) {
    console.error('Failed to save to offline queue:', e)
    snackbarStore.error(t('test.saveFailed'))
  }
  return false
}

const flushSubmitQueue = async () => {
  const raw = localStorage.getItem(QUEUE_KEY)
  if (!raw) return
  let queue
  try { queue = JSON.parse(raw) } catch { return }
  if (!queue.length) return

  const remaining = []
  for (const item of queue) {
    try {
      await api.post('/tests/submit', item.data)
    } catch {
      remaining.push(item)
    }
  }
  if (remaining.length === 0) {
    localStorage.removeItem(QUEUE_KEY)
    snackbarStore.success(t('test.queueFlushed') || 'РЎР°Т›С‚Р°Р»Т“Р°РЅ РЅУ™С‚РёР¶РµР»РµСЂ СЃУ™С‚С‚С– Р¶С–Р±РµСЂС–Р»РґС–!')
  } else {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(remaining))
  }
}

// Check if user is LIMITED_STUDENT
const isLimitedStudent = computed(() => {
  return authStore.user?.role === 'LIMITED_STUDENT'
})

// Test modes configuration
const testModes = computed(() => [
  {
    value: 'QUESTIONS_40',
    title: 'test.modes.questions40',
    description: 'test.modes.questions40Desc',
    icon: 'mdi-school',
    color: 'primary',
    features: [
      t('test.features.40questions'),
      t('test.features.noTimeLimit'),
      t('test.features.withExplanation'),
      t('test.features.learningMode')
    ]
  },
  {
    value: 'EXAM',
    title: 'test.modes.exam',
    description: 'test.modes.examDesc',
    icon: 'mdi-certificate',
    color: 'warning',
    features: [
      t('test.features.40questions'),
      t('test.features.40minutes'),
      t('test.features.noExplanation'),
      t('test.features.examMode')
    ]
  },
  {
    value: 'TRAINING_MODE',
    title: 'test.modes.training',
    description: 'test.modes.trainingDesc',
    icon: 'mdi-dumbbell',
    color: 'success',
    features: [
      t('test.features.5to40questions'),
      t('test.features.anyCategory'),
      t('test.features.alwaysExplanation'),
      t('test.features.trainingMode')
    ]
  },
  {
    value: 'ONLINE_LESSONS',
    title: 'test.modes.onlineLessons',
    description: 'test.modes.onlineLessonsDesc',
    icon: 'mdi-video-box',
    color: 'info',
    features: [
      t('test.features.videoLessons'),
      t('test.features.byTopics'),
      t('test.features.anytime'),
      t('test.features.onlineLearning')
    ]
  }
])

const categories = computed(() => {
  // Sort topics: numbered topics first by number, then unnumbered alphabetically
  const sortedTopics = [...availableTopics.value].sort((a, b) => {
    const numA = getTopicNumber(a)
    const numB = getTopicNumber(b)
    
    // Both have numbers - sort by number
    if (numA && numB) {
      return numA - numB
    }
    // Only A has number - A comes first
    if (numA && !numB) {
      return -1
    }
    // Only B has number - B comes first
    if (!numA && numB) {
      return 1
    }
    // Neither has number - sort alphabetically
    const textA = getTopicText(a, selectedLanguage.value).toLowerCase()
    const textB = getTopicText(b, selectedLanguage.value).toLowerCase()
    return textA.localeCompare(textB, 'ru')
  })
  
  const topicItems = sortedTopics.map(topic => ({
    title: formatTopicDisplay(topic, selectedLanguage.value),
    value: topic
  }))
  
  return [
    { title: t('test.allTopics'), value: 'all' },
    ...topicItems
  ]
})

const topicCategories = computed(() => {
  // Add "All" option at the beginning
  const allOption = {
    title: t('test.allTopics'),
    value: 'ALL_TOPICS'
  }
  
  // Sort topics: numbered topics first by number, then unnumbered alphabetically
  const sortedTopics = [...availableTopics.value].sort((a, b) => {
    const numA = getTopicNumber(a)
    const numB = getTopicNumber(b)
    
    // Both have numbers - sort by number
    if (numA && numB) {
      return numA - numB
    }
    // Only A has number - A comes first
    if (numA && !numB) {
      return -1
    }
    // Only B has number - B comes first
    if (!numA && numB) {
      return 1
    }
    // Neither has number - sort alphabetically
    const textA = getTopicText(a, selectedLanguage.value).toLowerCase()
    const textB = getTopicText(b, selectedLanguage.value).toLowerCase()
    return textA.localeCompare(textB, 'ru')
  })
  
  const topicOptions = sortedTopics.map(topic => ({
    title: formatTopicDisplay(topic, selectedLanguage.value),
    value: topic
  }))
  
  return [allOption, ...topicOptions]
})

// Computed
const currentTest = computed(() => {
  if (!tests.value || tests.value.length === 0) return null
  return tests.value[currentQuestionIndex.value]
})

// Normalize video path to absolute URL served by backend static /videos
const NO_VIDEO_SENTINELS = new Set(['__NO_VIDEO__', '__LOGO_MEDICAL__'])

const normalizeVideoSrc = (src) => {
  if (!src) return null

  const trimmed = src.trim()
  if (!trimmed) return null

  // Sentinel values mean "no real video" — return null so animations show instead
  if (NO_VIDEO_SENTINELS.has(trimmed)) return null

  if (trimmed.startsWith('data:')) {
    return trimmed
  }

  if (/^(?:https?:)?\/\//i.test(trimmed)) {
    try {
      const parsedUrl = new URL(trimmed, window.location.origin)
      const absoluteVideoIndex = parsedUrl.pathname.indexOf('/videos/')
      if (absoluteVideoIndex !== -1) {
        return withBasePath(parsedUrl.pathname.slice(absoluteVideoIndex))
      }
      return parsedUrl.toString()
    } catch (_) {
      return trimmed
    }
  }

  let normalized = trimmed

  const withSlashSegmentIndex = normalized.indexOf('/videos/')
  if (withSlashSegmentIndex !== -1) {
    normalized = normalized.slice(withSlashSegmentIndex)
  } else {
    const segmentIndex = normalized.indexOf('videos/')
    if (segmentIndex !== -1) {
      normalized = normalized.slice(segmentIndex)
    }
  }

  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`
  }

  if (!normalized.startsWith('/videos/')) {
    const sanitized = normalized.replace(/^\/+/, '').replace(/^videos\//, '')
    normalized = `/videos/${sanitized}`
  }

  return withBasePath(normalized)
}

const {
  showLessons,
  lessonCategories,
  selectedLessonCategory,
  currentLessons,
  currentLesson,
  lessonsLoading,
  lessonVideoPlayer,
  lessonVideoSrc,
  resolvedLessonVideoUrl,
  selectLessonCategory,
  selectLesson,
  onLessonVideoError,
  backToCategories,
  backToModeSelection,
  startOnlineLessons
} = useOnlineLessons({
  api,
  snackbarStore,
  t,
  locale,
  nextTick,
  normalizeVideoSrc,
  selectedMode,
  authStore,
  onReturnToModeSelection: async () => {
    await Promise.all([
      loadIncorrectQuestionsSummary(),
      loadAssignedTests()
    ])
  }
})

const currentVideo = computed(() => {
  if (!currentTest.value) return null
  
  // video1 = РЎТ±СЂР°Т› РІРёРґРµРѕСЃС‹ (Р¶Р°СѓР°Рї Р±РµСЂСѓ Р°Р»РґС‹РЅРґР° РєУ©СЂСЃРµС‚С–Р»РµРґС–)
  // video2 = РўТЇСЃС–РЅРґС–СЂРјРµ РІРёРґРµРѕСЃС‹ (Р¶Р°СѓР°Рї Р±РµСЂРіРµРЅРЅРµРЅ РєРµР№С–РЅ РєУ©СЂСЃРµС‚С–Р»РµРґС–)
  const surakVideo = normalizeVideoSrc(currentTest.value.video1)
  const tusindirmeVideo = normalizeVideoSrc(currentTest.value.video2)
  
  // Р­РљР—РђРњР•Рќ СЂРµР¶РёРјС–РЅРґРµ - У™СЂТ›Р°С€Р°РЅ С‚РµРє СЃТ±СЂР°Т› РІРёРґРµРѕСЃС‹ (video1) РєУ©СЂСЃРµС‚С–Р»РµРґС–
  // Р­РєР·Р°РјРµРЅРґРµ С‚ТЇСЃС–РЅРґС–СЂРјРµ РІРёРґРµРѕСЃС‹ РєУ©СЂСЃРµС‚С–Р»РјРµР№РґС–!
  if (isExamMode.value || selectedMode.value === 'EXAM') {
    return surakVideo
  }
  
  // Р‘Р°СЃТ›Р° СЂРµР¶РёРјРґРµСЂРґРµ:
  // - Р–Р°СѓР°Рї Р±РµСЂС–Р»РјРµРіРµРЅ РєРµР·РґРµ - СЃТ±СЂР°Т› РІРёРґРµРѕСЃС‹ (video1)
  // - Р–Р°СѓР°Рї Р±РµСЂС–Р»РіРµРЅРЅРµРЅ РєРµР№С–РЅ - С‚ТЇСЃС–РЅРґС–СЂРјРµ РІРёРґРµРѕСЃС‹ (video2), РµРіРµСЂ Р±Р°СЂ Р±РѕР»СЃР°
  if (answered.value && tusindirmeVideo) {
    return tusindirmeVideo
  }
  return surakVideo
})

const testVideoFallbackUrl = computed(() => {
  const primarySrc = currentVideo.value
  if (!primarySrc) return null

  const videoPathIndex = primarySrc.indexOf('/videos/')
  if (videoPathIndex === -1) return null

  const videoPath = primarySrc.slice(videoPathIndex)
  // Fallback origin must be configured via VITE_MEDIA_FALLBACK_ORIGIN.
  // No origin → no fallback URL (primary src is used as-is).
  const explicitOrigin = import.meta.env.VITE_MEDIA_FALLBACK_ORIGIN
  if (!explicitOrigin) return null
  const fallbackBase = explicitOrigin.replace(/\/+$/, '')
  return `${fallbackBase}${videoPath}`
})

const resolvedCurrentVideo = computed(() => testVideoSrc.value || currentVideo.value)

const answeredQuestions = computed(() => userAnswers.value.length)

const progressPercentage = computed(() => {
  const testsLength = tests.value?.length || 0
  if (testsLength === 0) return 0
  return (answeredQuestions.value / testsLength) * 100
})

const showCorrectAnswer = computed(() => {
  if (selectedMode.value === 'EXAM') return false
  return answered.value
})

const showExplanation = computed(() => {
  if (selectedMode.value === 'TRAINING_MODE') return answered.value
  if (selectedMode.value === 'QUESTIONS_40') {
    return answered.value && userAnswer.value === currentTest.value?.correctAnswer
  }
  return false
})

const testPassed = computed(() => results.value.score >= 80)

const isMobile = computed(() => {
  if (typeof window !== 'undefined') {
    return window.innerWidth <= 768
  }
  return false
})

const {
  trainingVariant,
  modeCardState,
  showTopicsSelector,
  showExamTopicsInfo,
  showTopicsHint,
  compactMistakeModeCard,
  inlineStartBarOnMobile,
  mistakeReviewToggleLabel,
  assignedTrainingToggleLabel,
  mistakeReviewCountHeading,
  startButtonIcon,
  startButtonLabel,
  canStartConfiguredMode
} = useTrainingModeSetup({
  t,
  isMobile,
  isExamMode,
  isMistakeReviewMode,
  isAssignedTestMode,
  selectedTopics,
  assignedTestsLoading,
  hasAssignedTests,
  currentAssignedTest,
  incorrectQuestionsCount,
  incorrectQuestionsLoading,
  mistakeReviewCountPreset,
  mistakeReviewCountOptions,
  resolvedMistakeQuestionCount
})

// Methods
const getQuestionText = (test) => {
  const key = `question${locale.value.charAt(0).toUpperCase() + locale.value.slice(1)}`
  return test[key] || test.questionRu
}

const getAnswers = (test) => {
  if (!test) {
    return [];
  }
  
  const key = `answers${locale.value.charAt(0).toUpperCase() + locale.value.slice(1)}`;
  const fallbackKey = 'answersRu';
  
  try {
    let originalAnswers = [];
    
    // Try primary language
    if (test[key]) {
      const parsed = typeof test[key] === 'string' ? JSON.parse(test[key]) : test[key];
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Filter out empty answers and use fallback for empty ones
        originalAnswers = parsed.map((answer, index) => {
          if (!answer || answer.trim() === '') {
            // If this answer is empty, try to get it from Russian fallback
            if (test[fallbackKey]) {
              try {
                const fallbackParsed = typeof test[fallbackKey] === 'string' ? JSON.parse(test[fallbackKey]) : test[fallbackKey];
                return fallbackParsed[index] || answer;
              } catch (e) {
                return answer;
              }
            }
          }
          return answer;
        });
        
        // Only use if we have valid answers
        if (originalAnswers.some(answer => answer && answer.trim() !== '')) {
          // Keep original answers array (even with some empty entries now filled)
        } else {
          originalAnswers = [];
        }
      }
    }
    
    // Fallback to Russian if no valid answers in primary language
    if (originalAnswers.length === 0 && test[fallbackKey]) {
      const parsed = typeof test[fallbackKey] === 'string' ? JSON.parse(test[fallbackKey]) : test[fallbackKey];
      if (Array.isArray(parsed) && parsed.length > 0) {
        originalAnswers = parsed;
      }
    }
    
    // If test has shuffle map, apply it to maintain consistent order
    if (test._shuffleMap && Array.isArray(test._shuffleMap) && originalAnswers.length > 0) {
      return test._shuffleMap.map(originalIndex => {
        const answer = originalAnswers[originalIndex]
        return answer == null ? '' : String(answer)
      })
    }
    
    return originalAnswers.map(answer => answer == null ? '' : String(answer))
  } catch (error) {
    return [];
  }
}

const getAnswerEntries = (test) => {
  // `actualIndex` is the position in the (possibly shuffled) array returned by
  // getAnswers(). When the test has been shuffled, `test.correctAnswer` was already
  // rewritten to the shuffled position (see startTest → newCorrectIndex), and
  // userAnswer is captured in the same shuffled coordinate space — so this index
  // is directly comparable to test.correctAnswer.
  return getAnswers(test)
    .map((text, actualIndex) => ({
      text: String(text || ''),
      actualIndex
    }))
    .filter(entry => entry.text.trim() !== '')
}

const findSavedAnswerForQuestion = (questionIndex, answersList = userAnswers.value, testsList = tests.value) => {
  if (!Array.isArray(answersList) || !Array.isArray(testsList)) return null

  const targetTest = testsList[questionIndex]
  if (!targetTest) return null

  return answersList.find(answer =>
    answer?.questionId === targetTest.id ||
    answer?.questionIndex === questionIndex
  ) || null
}

const getExplanation = (test) => {
  const currentLocale = locale.value || 'kz'
  const key = `explanation${currentLocale.charAt(0).toUpperCase() + currentLocale.slice(1)}`
  let explanation = test[key] || test.explanationRu || ''
  
  // Р•РіРµСЂ С‚ТЇСЃС–РЅС–РєС‚РµРјРµРґРµ Р±Р°СЂР»С‹Т› ТЇС€ С‚С–Р» Р±Р°СЂ Р±РѕР»СЃР°, С‚РµРє С‚Р°ТЈРґР°Р»Т“Р°РЅ С‚С–Р»РґС– РєУ©СЂСЃРµС‚Сѓ
  if (explanation.includes('РћР±СЉСЏСЃРЅРµРЅРёРµ РЅР° РљР—:') || explanation.includes('РћР±СЉСЏСЃРЅРµРЅРёРµ РЅР° РђРќР“:')) {
    const lines = explanation.split('\n')
    
    if (currentLocale === 'kz') {
      // ТљР°Р·Р°Т› С‚С–Р»С– ТЇС€С–РЅ "РћР±СЉСЏСЃРЅРµРЅРёРµ РЅР° РљР—:" Р°СЂТ›С‹Р»С‹ Р±Р°СЃС‚Р°Рї, РєРµР»РµСЃС– "РћР±СЉСЏСЃРЅРµРЅРёРµ РЅР° РђРќР“:" РґРµР№С–РЅ
      const kzStart = lines.findIndex(line => line.includes('РћР±СЉСЏСЃРЅРµРЅРёРµ РЅР° РљР—:'))
      if (kzStart !== -1) {
        const kzEnd = lines.findIndex((line, index) => 
          index > kzStart && line.includes('РћР±СЉСЏСЃРЅРµРЅРёРµ РЅР° РђРќР“:'))
        const kzLines = kzEnd !== -1 ? lines.slice(kzStart, kzEnd) : lines.slice(kzStart)
        explanation = kzLines.join('\n').replace('РћР±СЉСЏСЃРЅРµРЅРёРµ РЅР° РљР—: ', '').trim()
      }
    } else if (currentLocale === 'en') {
      // РђТ“С‹Р»С€С‹РЅ С‚С–Р»С– ТЇС€С–РЅ "РћР±СЉСЏСЃРЅРµРЅРёРµ РЅР° РђРќР“:" Р°СЂТ›С‹Р»С‹ Р±Р°СЃС‚Р°Рї
      const enStart = lines.findIndex(line => line.includes('РћР±СЉСЏСЃРЅРµРЅРёРµ РЅР° РђРќР“:'))
      if (enStart !== -1) {
        const enLines = lines.slice(enStart)
        explanation = enLines.join('\n').replace('РћР±СЉСЏСЃРЅРµРЅРёРµ РЅР° РђРќР“: ', '').trim()
      }
    } else {
      // РћСЂС‹СЃ С‚С–Р»С– ТЇС€С–РЅ "РћР±СЉСЏСЃРЅРµРЅРёРµ РЅР° РљР—:" РґРµР№С–РЅ
      const kzStart = lines.findIndex(line => line.includes('РћР±СЉСЏСЃРЅРµРЅРёРµ РЅР° РљР—:'))
      if (kzStart !== -1) {
        const ruLines = lines.slice(0, kzStart)
        explanation = ruLines.join('\n').trim()
      }
    }
  }
  
  return explanation
}

const getAnswerClass = (index) => {
  // Р­РєР·Р°РјРµРЅ СЂРµР¶РёРјС–РЅРґРµ С‚РµСЃС‚ Р°СЏТ›С‚Р°Р»Т“Р°РЅТ“Р° РґРµР№С–РЅ РґТ±СЂС‹СЃ/Р±Т±СЂС‹СЃ РєУ©СЂСЃРµС‚РїРµР№РјС–Р·
  if (isExamMode.value && !showResults.value) {
    return userAnswer.value === index ? 'selected' : ''
  }
  
  if (!answered.value) {
    return userAnswer.value === index ? 'selected' : ''
  }
  if (index === currentTest.value?.correctAnswer) {
    return 'correct'
  }
  if (userAnswer.value === index && index !== currentTest.value?.correctAnswer) {
    return 'incorrect'
  }
  return ''
}

// РРЅРґРµРєСЃ Р±РѕР№С‹РЅС€Р° Р¶Р°СѓР°Рї Р°Р»Сѓ (РЅУ™С‚РёР¶РµР»РµСЂ ТЇС€С–РЅ)
const getAnswerByIndex = (test, answerIndex) => {
  const answers = getAnswers(test)
  return answers[answerIndex] || ''
}

// Р’РёРґРµРѕ URL Р°Р»Сѓ (РЅУ™С‚РёР¶РµР»РµСЂ С‚С–Р·С–РјС– ТЇС€С–РЅ РґРµ)
const getVideoUrl = (test) => {
  if (!test) return null
  const videoName = test.video1 || test.video2 || test.videoKz || test.videoRu || test.video
  return normalizeVideoSrc(videoName)
}

const ANSWER_TOUCH_MOVE_THRESHOLD = 18

const clearAnswerPointerState = () => {
  activeAnswerPointer.value = null
  activeAnswerPointerStart.value = null
}

const handleAnswerPointerDown = (index, event) => {
  if (!event || (event.pointerType !== 'touch' && event.pointerType !== 'pen')) {
    return
  }

  activeAnswerPointer.value = {
    index,
    pointerId: event.pointerId
  }
  activeAnswerPointerStart.value = {
    x: event.clientX,
    y: event.clientY
  }

  if (typeof event.currentTarget?.setPointerCapture === 'function') {
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch (_) {
      // Ignore browsers that reject pointer capture for synthetic events.
    }
  }
}

const handleAnswerPointerUp = (index, event) => {
  const activePointer = activeAnswerPointer.value
  const startPoint = activeAnswerPointerStart.value

  if (
    !event ||
    !activePointer ||
    activePointer.index !== index ||
    activePointer.pointerId !== event.pointerId
  ) {
    return
  }

  clearAnswerPointerState()

  const movedX = Math.abs((event.clientX ?? 0) - (startPoint?.x ?? 0))
  const movedY = Math.abs((event.clientY ?? 0) - (startPoint?.y ?? 0))

  if (movedX > ANSWER_TOUCH_MOVE_THRESHOLD || movedY > ANSWER_TOUCH_MOVE_THRESHOLD) {
    return
  }

  lastTouchAnswerSelectionAt.value = Date.now()
  selectAnswer(index)
}

const handleAnswerClick = (index) => {
  if (Date.now() - lastTouchAnswerSelectionAt.value < 700) {
    return
  }

  selectAnswer(index)
}

const selectAnswer = (index) => {
  if (!currentTest.value) return

  const answers = getAnswers(currentTest.value)
  const selectedAnswerText = answers[index]
  if (selectedAnswerText == null || String(selectedAnswerText).trim() === '') {
    return
  }

  // Р­РєР·Р°РјРµРЅ СЂРµР¶РёРјС–РЅРґРµ Р¶Р°СѓР°РїС‚С‹ "РљРµР»РµСЃС–" Р±Р°С‚С‹СЂРјР°СЃС‹РЅ Р±Р°СЃТ›Р°РЅТ“Р° РґРµР№С–РЅ Р°СѓС‹СЃС‚С‹СЂСѓТ“Р° Р±РѕР»Р°РґС‹
  if (isExamMode.value) {
    userAnswer.value = index
    saveTestState()
    // Р­РєР·Р°РјРµРЅРґРµ РґТ±СЂС‹СЃ/Р±Т±СЂС‹СЃ РєУ©СЂСЃРµС‚РїРµР№РјС–Р·, С‚РµРє С‚Р°ТЈРґР°СѓРґС‹ Р±РµР»РіС–Р»РµР№РјС–Р·
    return
  }
  
  // Р‘Р°СЃТ›Р° СЂРµР¶РёРјРґРµСЂРґРµ РµСЃРєС– Р»РѕРіРёРєР°
  if (!answered.value) {
    userAnswer.value = index
    saveTestState()
    
    // Р‘Р°СЂР»С‹Т› СЂРµР¶РёРјРґРµСЂРґРµ Р¶Р°СѓР°РїС‚С‹ С‚Р°ТЈРґР°Т“Р°РЅ РєРµР·РґРµ Р±С–СЂРґРµРЅ РєУ©СЂСЃРµС‚Сѓ
    submitAnswer()
  }
}

const submitAnswer = async () => {
  if (userAnswer.value === null || !currentTest.value) return

  answered.value = true
  const isCorrect = userAnswer.value === currentTest.value.correctAnswer

  // Detect index inconsistency between _shuffleMap and correctAnswer/_originalCorrectAnswer.
  // If found, report it to /api/errors so admins can review in /admin/error-logs.
  try {
    const t = currentTest.value
    const sm = t._shuffleMap
    if (Array.isArray(sm)) {
      const remapped = sm[t.correctAnswer]
      const orig = t._originalCorrectAnswer
      // Both should resolve to the same original answer
      if (orig != null && remapped != null && remapped !== orig) {
        const visible = getAnswerEntries(t).map((e, i) => `[${i}] ${String(e.text).slice(0, 60)}`)
        const payload = {
          userId: authStore.user?.id,
          userPhone: authStore.user?.phone,
          userName: authStore.user?.fio,
          errorType: 'TEST_INDEX_MISMATCH',
          errorCode: 'SHUFFLE_INCONSISTENT',
          message: `Test #${t.id}: shuffleMap[correctAnswer=${t.correctAnswer}]=${remapped} but _originalCorrectAnswer=${orig}`,
          url: window.location.pathname,
          method: 'GET',
          requestBody: {
            testId: t.id,
            lang: locale.value,
            mode: selectedMode.value,
            userAnswer: userAnswer.value,
            correctAnswer: t.correctAnswer,
            _originalCorrectAnswer: orig,
            _shuffleMap: sm,
            visibleAnswers: visible,
            pickedText: visible[userAnswer.value],
            correctText: visible[t.correctAnswer]
          }
        }
        api.post('/errors', payload).catch(() => { /* ignore reporting failure */ })
      }
    }
  } catch (e) { /* never let logging break the flow */ }
  
  // Update question state
  questionStates.value[currentQuestionIndex.value] = isCorrect ? 'correct' : 'wrong'
  
  userAnswers.value.push({
    questionId: currentTest.value.id,
    userAnswer: userAnswer.value,
    originalUserAnswer: Array.isArray(currentTest.value._shuffleMap) ? currentTest.value._shuffleMap[userAnswer.value] : userAnswer.value,
    correctAnswer: currentTest.value.correctAnswer,
    originalCorrectAnswer: currentTest.value._originalCorrectAnswer ?? currentTest.value.correctAnswer,
    isCorrect: isCorrect
  })

  // Қатемен жұмыс режимінде дұрыс жауап берсе — санақты бірден азайту
  if (isMistakeReviewMode.value && isCorrect) {
    decrementIncorrectCount()
  }

  saveTestState()

  await nextTick()
  revealAnsweredContent()
}

const nextQuestion = async () => {
  // Mark current question state before moving
  if (!answered.value) {
    questionStates.value[currentQuestionIndex.value] = 'skipped'
  }
  
  if (currentQuestionIndex.value < (tests.value?.length || 0) - 1) {
    blurActiveControl()
    currentQuestionIndex.value++
    userAnswer.value = null
    answered.value = false
    playingVideo2.value = false
    saveTestState()
    await nextTick()
    scrollToCurrentQuestion()
    scrollToVideoSection()
    focusQuestionHeading()
  }
}

// Р­РєР·Р°РјРµРЅ СЂРµР¶РёРјС–РЅРµ Р°СЂРЅР°Р»Т“Р°РЅ РєРµР»РµСЃС– СЃТ±СЂР°Т›Т›Р° У©С‚Сѓ С„СѓРЅРєС†РёСЏСЃС‹
const nextQuestionExam = async () => {
  // Р–Р°СѓР°РїС‚С‹ examAnswers РјР°СЃСЃРёРІС–РЅРµ СЃР°Т›С‚Р°Сѓ
  if (userAnswer.value !== null && currentTest.value) {
    const isCorrect = userAnswer.value === currentTest.value.correctAnswer
    
    // РўРµСЃС‚ РѕР±СЉРµРєС‚С–СЃС–РЅ С‚РѕР»С‹Т› РєУ©С€С–СЂСѓ (СЂРµР°РєС‚РёРІС‚С–Р»С–РєС‚РµРЅ Р°СЂС‹Р»Сѓ ТЇС€С–РЅ)
    const testCopy = JSON.parse(JSON.stringify(currentTest.value))
    
    // Р•РіРµСЂ Р±Т±Р» СЃТ±СЂР°Т›Т›Р° Р±Т±СЂС‹РЅ Р¶Р°СѓР°Рї Р±РµСЂС–Р»РјРµРіРµРЅ Р±РѕР»СЃР°
    const existingAnswerIndex = userAnswers.value.findIndex(a => a.questionId === currentTest.value.id)
    if (existingAnswerIndex === -1) {
      userAnswers.value.push({
        questionId: currentTest.value.id,
        questionIndex: currentQuestionIndex.value,
        userAnswer: userAnswer.value,
        originalUserAnswer: Array.isArray(currentTest.value._shuffleMap) ? currentTest.value._shuffleMap[userAnswer.value] : userAnswer.value,
        correctAnswer: currentTest.value.correctAnswer,
        originalCorrectAnswer: currentTest.value._originalCorrectAnswer ?? currentTest.value.correctAnswer,
        isCorrect: isCorrect,
        test: testCopy // РўРѕР»С‹Т› РєУ©С€С–СЂРјРµ СЃР°Т›С‚Р°Сѓ
      })
    } else {
      // Р–Р°СѓР°РїС‚С‹ Р¶Р°ТЈР°СЂС‚Сѓ
      userAnswers.value[existingAnswerIndex] = {
        questionId: currentTest.value.id,
        questionIndex: currentQuestionIndex.value,
        userAnswer: userAnswer.value,
        originalUserAnswer: Array.isArray(currentTest.value._shuffleMap) ? currentTest.value._shuffleMap[userAnswer.value] : userAnswer.value,
        correctAnswer: currentTest.value.correctAnswer,
        originalCorrectAnswer: currentTest.value._originalCorrectAnswer ?? currentTest.value.correctAnswer,
        isCorrect: isCorrect,
        test: testCopy
      }
    }
    
    // РљТЇР№РґС– Р±РµР»РіС–Р»РµСѓ (Р±С–СЂР°Т› С‚ТЇСЃС–РЅ РєУ©СЂСЃРµС‚РїРµСѓ)
    questionStates.value[currentQuestionIndex.value] = 'answered'
  }
  
  if (currentQuestionIndex.value < (tests.value?.length || 0) - 1) {
    blurActiveControl()
    currentQuestionIndex.value++
    userAnswer.value = null
    saveTestState()
    await nextTick()
    scrollToCurrentQuestion()
    scrollToVideoSection()
    focusQuestionHeading()
  }
}

// Р­РєР·Р°РјРµРЅ Р°СЏТ›С‚Р°Сѓ С„СѓРЅРєС†РёСЏСЃС‹
const finishExam = async () => {
  // РЎРѕТЈТ“С‹ СЃТ±СЂР°Т›С‚С‹ТЈ Р¶Р°СѓР°Р±С‹РЅ СЃР°Т›С‚Р°Сѓ
  if (userAnswer.value !== null && currentTest.value) {
    const isCorrect = userAnswer.value === currentTest.value.correctAnswer
    const testCopy = JSON.parse(JSON.stringify(currentTest.value))
    const existingAnswerIndex = userAnswers.value.findIndex(a => a.questionId === currentTest.value.id)
    if (existingAnswerIndex === -1) {
      userAnswers.value.push({
        questionId: currentTest.value.id,
        questionIndex: currentQuestionIndex.value,
        userAnswer: userAnswer.value,
        originalUserAnswer: Array.isArray(currentTest.value._shuffleMap) ? currentTest.value._shuffleMap[userAnswer.value] : userAnswer.value,
        correctAnswer: currentTest.value.correctAnswer,
        originalCorrectAnswer: currentTest.value._originalCorrectAnswer ?? currentTest.value.correctAnswer,
        isCorrect: isCorrect,
        test: testCopy
      })
    } else {
      userAnswers.value[existingAnswerIndex] = {
        questionId: currentTest.value.id,
        questionIndex: currentQuestionIndex.value,
        userAnswer: userAnswer.value,
        originalUserAnswer: Array.isArray(currentTest.value._shuffleMap) ? currentTest.value._shuffleMap[userAnswer.value] : userAnswer.value,
        correctAnswer: currentTest.value.correctAnswer,
        originalCorrectAnswer: currentTest.value._originalCorrectAnswer ?? currentTest.value.correctAnswer,
        isCorrect: isCorrect,
        test: testCopy
      }
    }
  }
  
  // РќУ™С‚РёР¶РµР»РµСЂРґС– РµСЃРµРїС‚РµСѓ
  const correctCount = userAnswers.value.filter(a => a.isCorrect).length
  const wrongCount = userAnswers.value.filter(a => !a.isCorrect).length
  const totalQuestions = tests.value.length
  const skippedCount = totalQuestions - userAnswers.value.length
  const examDuration = 2400 // 40 РјРёРЅСѓС‚ СЃРµРєСѓРЅРґРїРµРЅ
  
  const calculatedResults = {
    score: Math.round((correctCount / totalQuestions) * 100),
    correctAnswers: correctCount,
    wrongAnswers: wrongCount,
    skippedQuestions: skippedCount,
    totalQuestions: totalQuestions,
    answeredQuestions: userAnswers.value.length,
    timeSpent: examDuration - timeLeft.value,
    wrongQuestionsList: userAnswers.value.filter(a => !a.isCorrect) // ТљР°С‚Рµ Р¶Р°СѓР°РїС‚Р°СЂ С‚С–Р·С–РјС–
  }
  
  results.value = calculatedResults
  // testPassed computed-С‚Р°РЅ РµСЃРµРїС‚РµР»РµРґС–, Т›РѕР»РјРµРЅ РѕСЂРЅР°С‚Сѓ Т›Р°Р¶РµС‚ РµРјРµСЃ
  
  // РќУ™С‚РёР¶РµР»РµСЂРґС– РєУ©СЂСЃРµС‚Сѓ
  await saveAndShowResults(calculatedResults)
}

const goToQuestion = (index) => {
  if (index >= 0 && index < tests.value.length) {
    blurActiveControl()
    // Only mark as skipped if not answered and not already marked
    if (!answered.value && currentQuestionIndex.value !== index && !questionStates.value[currentQuestionIndex.value]) {
      questionStates.value[currentQuestionIndex.value] = 'skipped'
    }
    currentQuestionIndex.value = index
    // Load the answer if exists
    const prevAnswer = findSavedAnswerForQuestion(index)
    if (prevAnswer) {
      userAnswer.value = prevAnswer.userAnswer
      answered.value = true
    } else {
      userAnswer.value = null
      answered.value = false
    }
    playingVideo2.value = false
    saveTestState()
    nextTick(() => {
      scrollToCurrentQuestion()
      scrollToVideoSection()
      focusQuestionHeading()
    })
  }
}

// Scroll question indicators to show current question
const scrollToCurrentQuestion = () => {
  nextTick(() => {
    const indicatorsContainer = document.querySelector('.question-indicators')
    const currentIndicator = document.querySelector('.question-indicator.current')
    
    if (indicatorsContainer && currentIndicator) {
      // Calculate the scroll position to center the current indicator perfectly
      const containerWidth = indicatorsContainer.clientWidth
      const indicatorLeft = currentIndicator.offsetLeft
      const indicatorWidth = currentIndicator.clientWidth
      
      // Center the current indicator in the container
      const targetScrollLeft = indicatorLeft - (containerWidth / 2) + (indicatorWidth / 2)
      
      // Ensure we don't scroll beyond boundaries
      const maxScroll = indicatorsContainer.scrollWidth - containerWidth
      const finalScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScroll))
      
      // Smooth scroll to position
      indicatorsContainer.scrollTo({
        left: finalScrollLeft,
        behavior: 'smooth'
      })
    }
  })
}

const previousQuestion = async () => {
  if (currentQuestionIndex.value > 0) {
    blurActiveControl()
    currentQuestionIndex.value--
    // Load the previous answer if exists
    const prevAnswer = findSavedAnswerForQuestion(currentQuestionIndex.value)
    if (prevAnswer) {
      userAnswer.value = prevAnswer.userAnswer
      answered.value = true
    } else {
      userAnswer.value = null
      answered.value = false
    }
    saveTestState()
    await nextTick()
    scrollToVideoSection()
    focusQuestionHeading()
    scrollToCurrentQuestion()
  }
}

// ТљР°СѓС–РїСЃС–Р· play() вЂ” DOM-РЅР°РЅ Р¶РѕР№С‹Р»Т“Р°РЅ video СЌР»РµРјРµРЅС‚С–РЅРґРµ AbortError Р±РѕР»РґС‹СЂРјР°Сѓ
const safePlay = (video) => {
  if (!video) return
  const playPromise = video.play()
  if (playPromise !== undefined) {
    playPromise.catch(() => { /* video removed from DOM or autoplay blocked */ })
  }
}

const onVideoReady = () => {
  videoError.value = false
  videoLoading.value = false
  safePlay(videoPlayer.value)

  if (isMobile.value && pendingMobileVideoAlign.value) {
    scheduleMobileVideoAlign()
  }
}

// Р‘С–СЂ СЃРµСЃСЃРёСЏРґР° Р±С–СЂ РІРёРґРµРѕТ“Р° 1 СЂРµС‚ Т“Р°РЅР° Т›Р°С‚Рµ Р¶С–Р±РµСЂРµРјС–Р·
const videoErrorsSent = new Set()

const onVideoError = async (e) => {
  const video = videoPlayer.value || e.target
  const mediaError = video?.error
  const code = mediaError?.code || 0
  // 1 = MEDIA_ERR_ABORTED — ignore (user navigation, video element teardown, etc.)
  if (code === 1) return
  const currentUrl = testVideoSrc.value || currentVideo.value
  const fallbackUrl = testVideoFallbackUrl.value
  if (fallbackUrl && currentUrl && currentUrl !== fallbackUrl) {
    videoLoading.value = true
    testVideoSrc.value = fallbackUrl
    await nextTick()
    if (videoPlayer.value) {
      videoPlayer.value.load()
      safePlay(videoPlayer.value)
    }
    return
  }
  // Primary AND fallback both failed → show NoVideoAnimation
  videoError.value = true
  videoLoading.value = false
  // Skip error logging for anonymous visitors
  if (!authStore.user?.id) return
  const videoSrc = video?.currentSrc || video?.src || resolvedCurrentVideo.value || currentVideo.value || 'unknown'
  // Deduplication: same video + same code should be logged once per session
  const errorKey = `${videoSrc}:${code}`
  if (videoErrorsSent.has(errorKey)) return
  videoErrorsSent.add(errorKey)
  const codeMap = { 2: 'MEDIA_ERR_NETWORK', 3: 'MEDIA_ERR_DECODE', 4: 'MEDIA_ERR_SRC_NOT_SUPPORTED' }
  const codeName = codeMap[code] || 'VIDEO_ERROR'
  api.post('/errors', {
    userId: authStore.user?.id,
    userPhone: authStore.user?.phone,
    userName: authStore.user?.fio,
    errorType: 'VIDEO_ERROR',
    errorCode: codeName,
    message: `Video failed to load: ${videoSrc} (${codeName}: ${mediaError?.message || ''})`,
    url: window.location.href,
    method: 'GET',
    requestBody: JSON.stringify({
      videoSrc: videoSrc,
      configuredSrc: resolvedCurrentVideo.value || currentVideo.value || null,
      fallbackSrc: testVideoFallbackUrl.value || null,
      currentSrc: video?.currentSrc || null,
      readyState: video?.readyState ?? null,
      networkState: video?.networkState ?? null,
      questionIndex: currentQuestionIndex.value,
      questionId: currentTest.value?.id,
      video1: currentTest.value?.video1 || null,
      video2: currentTest.value?.video2 || null,
      mediaErrorCode: code
    })
  }).catch(() => {})
}
const onVideoEnded = () => {
  // Р­РєР·Р°РјРµРЅ СЂРµР¶РёРјС–РЅРґРµ video2 auto-play Р¶Р°СЃР°РјР°Р№РјС‹Р·
  if (isExamMode.value || selectedMode.value === 'EXAM') {
    return
  }
  
  // video2 Р°СЏТ›С‚Р°Р»РґС‹ вЂ” Р±С–СЂ СЂРµС‚ РѕР№РЅР°С‚С‹Р»РґС‹, Т›Р°Р№С‚Р° РѕР№РЅР°С‚РїР°Р№РјС‹Р·
  if (playingVideo2.value) {
    playingVideo2.value = false
    return
  }
  
  // video1 Р°СЏТ›С‚Р°Р»РґС‹: Р–Р°СѓР°Рї Р±РµСЂС–Р»РіРµРЅРЅРµРЅ РєРµР№С–РЅ video2-РЅС– auto-play Р¶Р°СЃР°Сѓ (С‚РµРє Р±С–СЂ СЂРµС‚)
  if (answered.value && currentTest.value?.video2 && videoPlayer.value) {
    playingVideo2.value = true
    videoPlayer.value.load()
    safePlay(videoPlayer.value)
  }
}

const restartVideo = () => {
  if (videoPlayer.value) {
    videoPlayer.value.currentTime = 0
    safePlay(videoPlayer.value)
  }
}

const togglePlayPause = () => {
  if (videoPlayer.value) {
    if (videoPlayer.value.paused) {
      safePlay(videoPlayer.value)
    } else {
      videoPlayer.value.pause()
    }
  }
}

// Language change function
const changeLanguage = (newLang) => {
  try {
    // Change language
    locale.value = newLang
    selectedLanguage.value = newLang
    
    // Close dropdown
    showLanguageMenu.value = false
    
    snackbarStore.success(t('common.languageChanged'))
  } catch (error) {
    snackbarStore.error(t('common.errorOccurred'))
  }
}

// Get current language short name
const getCurrentLanguage = () => {
  const lang = availableLanguages.value.find(l => l.code === selectedLanguage.value)
  return lang ? lang.shortName : 'KZ'
}

const selectLanguage = (langCode) => {
  showLanguageMenu.value = false
  changeLanguage(langCode)
}

// Close dropdown when clicking outside
onMounted(() => {
  document.addEventListener('click', () => {
    showLanguageMenu.value = false
  })
})

onUnmounted(() => {
  document.removeEventListener('click', () => {
    showLanguageMenu.value = false
  })
})

onUnmounted(() => {
  clearMobileVideoAlignTimeouts()
})

// Toggle language menu
const toggleLanguageMenu = () => {
  showLanguageMenu.value = !showLanguageMenu.value
}

const blurActiveControl = () => {
  if (typeof document === 'undefined') return
  const active = document.activeElement
  if (active && typeof active.blur === 'function') {
    active.blur()
  }
}

const clearMobileVideoAlignTimeouts = () => {
  mobileVideoAlignTimeouts.forEach(clearTimeout)
  mobileVideoAlignTimeouts = []
}

const alignMobileVideoBelowToolbar = () => {
  if (typeof window === 'undefined' || !isMobile.value || !videoSectionRef.value) return

  const topbarHeight = testTopbarRef.value?.offsetHeight || 0
  const videoRect = videoSectionRef.value.getBoundingClientRect()
  const absoluteTop = Math.max(videoRect.top + window.scrollY - topbarHeight, 0)
  const scroller = testInterfaceRef.value

  if (scroller) {
    const scrollerRect = scroller.getBoundingClientRect()
    const targetTop = scroller.scrollTop + (videoRect.top - scrollerRect.top) - topbarHeight
    scroller.scrollTop = Math.max(targetTop, 0)
  }

  if (document.scrollingElement) document.scrollingElement.scrollTop = absoluteTop
  if (document.documentElement) document.documentElement.scrollTop = absoluteTop
  if (document.body) document.body.scrollTop = absoluteTop
  window.scrollTo({ top: absoluteTop, behavior: 'auto' })
}

const scheduleMobileVideoAlign = () => {
  if (!isMobile.value) return

  pendingMobileVideoAlign.value = true
  clearMobileVideoAlignTimeouts()

  const delays = [0, 32, 120, 260]

  delays.forEach((delay, index) => {
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        alignMobileVideoBelowToolbar()
        if (index === delays.length - 1) {
          pendingMobileVideoAlign.value = false
        }
      })
    }, delay)

    mobileVideoAlignTimeouts.push(timeoutId)
  })
}

const scrollToVideoSection = () => {
  if (typeof window === 'undefined') return

  // РњРѕР±РёР»СЊРґС–: .test-interface.fullscreen вЂ” fixed РєРѕРЅС‚РµР№РЅРµСЂ, scrollTop = 0 в†’ РІРёРґРµРѕ topbar-РґР°РЅ РєРµР№С–РЅ РєУ©СЂС–РЅРµРґС–
  if (isMobile.value && testInterfaceRef.value && videoSectionRef.value) {
    alignMobileVideoBelowToolbar()

    return
  }

  const element = videoSectionRef.value
  if (!element) return

  const rect = element.getBoundingClientRect()
  const offsetTop = rect.top + window.scrollY
  const padding = 80

  window.scrollTo({
    top: Math.max(offsetTop - padding, 0),
    behavior: 'smooth'
  })
}

const getElement = (target) => {
  if (!target) return null
  if (target.$el) return target.$el
  return target
}

const focusElement = (target) => {
  const el = getElement(target)
  if (!el || typeof el.focus !== 'function') {
    return
  }

  try {
    el.focus({ preventScroll: true })
  } catch {
    el.focus()
  }
}

const scrollElementIntoView = (target, options = {}) => {
  if (typeof window === 'undefined') return
  const el = getElement(target)
  if (!el || typeof el.scrollIntoView !== 'function') return

  el.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    ...options
  })
}

const revealAnsweredContent = () => {
  const runReveal = () => {
    if (isMobile.value && nextButtonRef.value) {
      focusElement(nextButtonRef.value)
      scrollElementIntoView(nextButtonRef.value, { block: 'end' })
      return
    }

    if (showExplanation.value && explanationRef.value) {
      scrollElementIntoView(explanationRef.value)
      focusElement(explanationRef.value)
    } else if (nextButtonRef.value) {
      focusElement(nextButtonRef.value)
      scrollElementIntoView(nextButtonRef.value, { block: 'center' })
    } else {
      scrollToVideoSection()
      focusQuestionHeading()
    }
  }

  if (typeof window !== 'undefined' && isMobile.value) {
    requestAnimationFrame(() => {
      requestAnimationFrame(runReveal)
    })
    return
  }

  runReveal()
}

const focusQuestionHeading = () => {
  focusElement(questionHeadingRef.value)
}

const clearTestState = () => {
  if (typeof window === 'undefined') return
  localStorage.removeItem(STORAGE_KEY)
}

const saveTestState = () => {
  if (typeof window === 'undefined' || isRestoring.value) return
  if (!testStarted.value || !(tests.value?.length)) {
    clearTestState()
    return
  }

  try {
    const payload = {
      selectedMode: selectedMode.value,
      selectedCategory: selectedCategory.value,
      isMistakeReviewMode: isMistakeReviewMode.value,
      isAssignedTestMode: isAssignedTestMode.value,
      selectedAssignedTestId: selectedAssignedTestId.value,
      activeAssignedTestId: activeAssignedTestId.value,
      activeAssignedTestMeta: activeAssignedTestMeta.value,
      activeTestVersion: activeTestVersion.value,
      questionCount: questionCount.value,
      tests: tests.value,
      currentQuestionIndex: currentQuestionIndex.value,
      userAnswer: userAnswer.value,
      answered: answered.value,
      userAnswers: userAnswers.value,
      startTime: startTime.value,
      timeLeft: timeLeft.value,
      savedAt: Date.now()
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch (error) {
    // Ignore persistence errors silently
  }
}

const restoreTestState = async () => {
  if (typeof window === 'undefined') return false

  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return false

  try {
    const data = JSON.parse(raw)
    if (!data || !Array.isArray(data.tests) || !data.tests.length) {
      clearTestState()
      return false
    }

    isRestoring.value = true
  selectedMode.value = data.selectedMode || null
  isMistakeReviewMode.value = !!data.isMistakeReviewMode
  isAssignedTestMode.value = !!data.isAssignedTestMode
  selectedAssignedTestId.value = data.selectedAssignedTestId ?? null
  activeAssignedTestId.value = data.activeAssignedTestId ?? null
  activeAssignedTestMeta.value = data.activeAssignedTestMeta ?? null
  activeTestVersion.value = data.activeTestVersion ?? defaultTestCatalogVersion
  // If previously saved category was 'general' or invalid, default to 'all'
  const savedCategory = data.selectedCategory
  selectedCategory.value = (savedCategory && savedCategory !== 'general') ? savedCategory : 'all'
    // Restore question count, but ensure it's at least 40 by default
    // This fixes the issue where users had saved 20 questions
    const savedQuestionCount = data.questionCount || 40
    questionCount.value = savedQuestionCount < 40 ? 40 : savedQuestionCount
    tests.value = data.tests
    currentQuestionIndex.value = data.currentQuestionIndex ?? 0
    playingVideo2.value = false
    userAnswers.value = Array.isArray(data.userAnswers) ? data.userAnswers : []
    const restoredAnswer = findSavedAnswerForQuestion(currentQuestionIndex.value, userAnswers.value, tests.value)
    userAnswer.value = restoredAnswer?.userAnswer ?? null
    answered.value = !!restoredAnswer
    startTime.value = data.startTime || Date.now()
    testStarted.value = true
    showResults.value = false

    if (typeof data.timeLeft === 'number') {
      const elapsed = data.savedAt ? Math.floor((Date.now() - data.savedAt) / 1000) : 0
      const updatedTimeLeft = data.timeLeft - elapsed
      timeLeft.value = updatedTimeLeft > 0 ? updatedTimeLeft : 0
    } else {
      timeLeft.value = 2400
    }

    await nextTick()

    if (answered.value) {
      revealAnsweredContent()
    } else {
      scrollToVideoSection()
      focusQuestionHeading()
    }

    isRestoring.value = false
    saveTestState()

    if (selectedMode.value === 'EXAM') {
      if (timeLeft.value > 0) {
        startTimer()
      } else {
        finishTest()
      }
    }

    return true
  } catch (error) {
    clearTestState()
    isRestoring.value = false
    return false
  }
}

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const formatAssignmentDate = (date) => {
  if (!date) return '-'
  return new Date(date).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const startTimer = () => {
  if (selectedMode.value !== 'EXAM' || timeLeft.value <= 0) {
    return
  }

  stopTimer()

  timerInterval.value = setInterval(() => {
    timeLeft.value = Math.max(timeLeft.value - 1, 0)
    saveTestState()

    if (timeLeft.value === 0) {
      finishTest()
    }
  }, 1000)
}

const stopTimer = () => {
  if (timerInterval.value) {
    clearInterval(timerInterval.value)
    timerInterval.value = null
  }
}

// Fisher-Yates shuffle algorithm for randomizing tests
const shuffleArray = (array) => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

const startTest = async (allowFullscreen = false) => {
  // Check if user is LIMITED_STUDENT
  if (isLimitedStudent.value) {
    snackbarStore.error(
      t('student.restrictions.accessDenied'),
      { timeout: 5000 }
    )
    return
  }

  loading.value = true
  activeTestVersion.value = null
  try {
    const {
      mode,
      isMistakeMode,
      isAssignedMode,
      requiredCount,
      params: requestParams
    } = buildTestFetchRequest({
      isExamMode: isExamMode.value,
      isMistakeReviewMode: isMistakeReviewMode.value,
      isAssignedTestMode: isAssignedTestMode.value,
      resolvedMistakeQuestionCount: resolvedMistakeQuestionCount.value,
      currentAssignedTest: currentAssignedTest.value,
      questionCount: questionCount.value,
      selectedTopics: selectedTopics.value
    })

    // CRITICAL: Set selectedMode for proper explanation display
    selectedMode.value = mode

    if (isMistakeMode && requiredCount <= 0) {
      snackbarStore.error(t('test.mistakeReviewNoQuestions'))
      return
    }

    if (isAssignedMode && !currentAssignedTest.value) {
      snackbarStore.error(t('test.noAssignedTests'))
      return
    }
    
    const params = {
      mode: mode,
      limit: requiredCount,
      // РњРђТўР«Р—Р”Р«: Р‘Т±СЂС‹РЅ Р¶Р°СѓР°Рї Р±РµСЂС–Р»РіРµРЅ С‚РµСЃС‚С‚РµСЂРґС– Т›Р°Р№С‚Р°Р»Р°РјР°Сѓ
      excludeAnswered: isMistakeMode || isAssignedMode ? 'false' : 'true'
    }

    if (isMistakeMode) {
      params.incorrectOnly = 'true'
    }

    if (isAssignedMode) {
      params.assignedTestId = currentAssignedTest.value.id
    }

    // Add topic filter for selected topics (multiple)
    // Don't send topics param if "ALL_TOPICS" is selected
    // РњРђТўР«Р—Р”Р«: ;;; Р±У©Р»РіС–С€ Т›РѕР»РґР°РЅР°РјС‹Р·, СЃРµР±РµР±С– С‚Р°Т›С‹СЂС‹Рї Р°С‚Р°СѓР»Р°СЂС‹РЅРґР° ТЇС‚С–СЂ Р±РѕР»СѓС‹ РјТЇРјРєС–РЅ
    if (!isMistakeMode && !isAssignedMode && selectedTopics.value && selectedTopics.value.length > 0) {
      const topicsToSend = selectedTopics.value.filter(t => t !== 'ALL_TOPICS')
      if (topicsToSend.length > 0) {
        params.topics = topicsToSend.join(';;;')
      }
    }
    
    const response = await api.post('/tests/fetch', requestParams)
    activeTestVersion.value = response.data?.testVersion || defaultTestCatalogVersion
    let testsData = response.data.tests || []
    
    // РўРµСЃС‚С‚РµСЂ С‚Р°Р±С‹Р»РјР°РґС‹
    if (testsData.length === 0) {
      snackbarStore.error(t('test.noTestsFound'))
      return
    }
    
    // Р–Р°ТЈР° Р°Р№РЅР°Р»С‹Рј Р±Р°СЃС‚Р°Р»РґС‹ С…Р°Р±Р°СЂР»Р°РјР°СЃС‹
    if (response.data.newRoundStarted) {
      console.log(`в™»пёЏ Р–Р°ТЈР° Р°Р№РЅР°Р»С‹Рј Р±Р°СЃС‚Р°Р»РґС‹! Exclude: ${response.data.excludedCount}`)
      snackbarStore.success(t('test.newRoundStarted') || 'Р–Р°ТЈР° Р°Р№РЅР°Р»С‹Рј Р±Р°СЃС‚Р°Р»РґС‹! рџЋ‰')
    }
    
    // ТљР°РЅС€Р° С‚РµСЃС‚ exclude Т›С‹Р»С‹РЅТ“Р°РЅС‹РЅ РєУ©СЂСЃРµС‚Сѓ (РµРіРµСЂ Р±Р°СЂ Р±РѕР»СЃР°)
    if (response.data.excludedCount > 0) {
      console.log(`рџ“‹ ${response.data.excludedCount} Р±Т±СЂС‹РЅ Р¶Р°СѓР°Рї Р±РµСЂС–Р»РіРµРЅ С‚РµСЃС‚ exclude Т›С‹Р»С‹РЅРґС‹`)
    }
    
    let selectedTests = testsData

    if (isMistakeMode) {
      questionCount.value = selectedTests.length
    }

    if (isAssignedMode) {
      questionCount.value = selectedTests.length
      activeAssignedTestId.value = currentAssignedTest.value.id
      activeAssignedTestMeta.value = response.data?.assignedTest || currentAssignedTest.value
    } else {
      clearAssignedTestSession()
    }
    
    // Randomize answer options for each test
    selectedTests = selectedTests.map(test => {
      // Get answers based on current locale
      const currentLocale = locale.value || 'kz'
      const answerKey = `answers${currentLocale.charAt(0).toUpperCase() + currentLocale.slice(1)}`
      
      let testAnswers = []
      try {
        if (test[answerKey]) {
          testAnswers = typeof test[answerKey] === 'string' ? JSON.parse(test[answerKey]) : test[answerKey]
        } else if (test.answersRu) {
          testAnswers = typeof test.answersRu === 'string' ? JSON.parse(test.answersRu) : test.answersRu
        } else if (test.answers) {
          testAnswers = Array.isArray(test.answers) ? test.answers : []
        }
      } catch (e) {
        testAnswers = []
      }
      
      if (testAnswers && Array.isArray(testAnswers) && testAnswers.length > 0) {
        const parsedCorrect = Number.parseInt(test.correctAnswer, 10)
        const originalCorrect = Number.isInteger(parsedCorrect) ? parsedCorrect : test.correctAnswer
        const answersWithIndex = testAnswers.map((answer, index) => ({ answer, originalIndex: index }))
        const shuffledAnswers = shuffleArray(answersWithIndex)
        
        // Save shuffled answers for all languages to maintain consistency
        const shuffledAnswerTexts = shuffledAnswers.map(item => item.answer)
        const newCorrectIndex = shuffledAnswers.findIndex(item => item.originalIndex === originalCorrect)
        
        return {
          ...test,
          // Keep original language-specific answers but also store shuffled version
          _originalCorrectAnswer: originalCorrect,
          correctAnswer: newCorrectIndex,
          _shuffledAnswers: shuffledAnswerTexts,
          _shuffleMap: shuffledAnswers.map(item => item.originalIndex)
        }
      }
      return test
    })
    
    tests.value = selectedTests

    // Reset all in-memory session state before a fresh run so old round answers
    // never bleed into the newly fetched question set.
    userAnswers.value = []
    questionStates.value = {}
    results.value = null
    pendingResults.value = null
    timeLeft.value = 2400

    // CRITICAL: Reset to first question
    currentQuestionIndex.value = 0
    userAnswer.value = null
    answered.value = false
    playingVideo2.value = false
    
    testStarted.value = true
    await nextTick()
    scrollToCurrentQuestion()
    scrollToVideoSection()
    focusQuestionHeading()
    if (allowFullscreen) {
      await enterTestFullscreen()
    }
    startTime.value = Date.now()
    startTimer()
    saveTestState()
    
    // Admin-Т“Р° С‚РµСЃС‚ Р±Р°СЃС‚Р°Р»Т“Р°РЅС‹РЅ С…Р°Р±Р°СЂР»Р°Сѓ
    if (socketStore.socket) {
      socketStore.socket.emit('test:start', {
        mode: selectedMode.value,
        topic: selectedTopics.value.length > 0 ? selectedTopics.value.join(', ') : t('test.allTopics'),
        questionsCount: tests.value.length
      })
    }
    
  } catch (error) {
    console.error('вќЊ startTest error:', error)
    
    // 401/403 вЂ” С‚РѕРєРµРЅ РµСЃРєС–СЂРіРµРЅ, Т›Р°Р№С‚Р° РєС–СЂСѓ РєРµСЂРµРє
    if (error.response?.status === 401 || error.response?.status === 403) {
      snackbarStore.error(t('common.sessionExpired') || 'РЎРµСЃСЃРёСЏ РјРµСЂР·С–РјС– Р°СЏТ›С‚Р°Р»РґС‹. ТљР°Р№С‚Р° РєС–СЂС–ТЈС–Р·.')
      authStore.logout()
      router.push('/login')
      return
    }
    
    // ТљР°С‚РµРЅС– error-logs-Т›Р° Р¶С–Р±РµСЂСѓ
    try {
      await api.post('/errors', {
        userId: authStore.user?.id,
        userPhone: authStore.user?.phone,
        userName: authStore.user?.fio,
        errorType: 'TEST_START_ERROR',
        errorCode: error.response?.status?.toString() || 'UNKNOWN',
        message: error.response?.data?.error || error.message || 'Test start failed',
        stack: error.stack,
        url: '/api/tests/fetch',
        method: 'POST'
      })
    } catch (logErr) {
      console.error('Failed to log error:', logErr)
    }
    
    snackbarStore.error(t('common.errorOccurred'))
    clearTestState()
  } finally {
    loading.value = false
  }
}

const finishTest = async () => {
  stopTimer()
  
  // Admin-Т“Р° С‚РµСЃС‚ Р°СЏТ›С‚Р°Р»Т“Р°РЅС‹РЅ С…Р°Р±Р°СЂР»Р°Сѓ
  if (socketStore.socket) {
    socketStore.socket.emit('test:end')
  }
  
  // Calculate all answered questions (both correct and wrong)
  const timeSpent = Math.floor((Date.now() - startTime.value) / 1000)
  const testsLength = tests.value?.length || 1
  
  // Count actual answers from userAnswers array
  const correctAnswers = userAnswers.value.filter(a => a.isCorrect).length
  const wrongAnswers = userAnswers.value.filter(a => !a.isCorrect).length
  const totalAnswered = userAnswers.value.length // Use actual length of answers array
  
  // Calculate skipped questions
  const skippedQuestions = testsLength - totalAnswered
  
  // Calculate score based on correct answers vs total questions
  const score = testsLength > 0 ? Math.round((correctAnswers / testsLength) * 100) : 0
  
  const calculatedResults = {
    score,
    correctAnswers,
    wrongAnswers,
    totalQuestions: testsLength,
    answeredQuestions: totalAnswered,
    skippedQuestions,
    timeSpent,
    wrongQuestionsList: userAnswers.value.filter(a => !a.isCorrect)
  }
  
  // Training Mode: Auto-save and show dialog
  if (selectedMode.value === 'TRAINING_MODE') {
    pendingResults.value = calculatedResults
    
    // РђРІС‚РѕРјР°С‚С‚С‹ С‚ТЇСЂРґРµ РЅУ™С‚РёР¶РµР»РµСЂРґС– СЃР°Т›С‚Р°Сѓ
    if (tests.value && tests.value.length > 0) {
      const submissionData = {
        testIds: tests.value.map(t => t.id),
        answers: userAnswers.value,
        mode: selectedMode.value,
        assignedTestId: activeAssignedTestId.value,
        score: calculatedResults.score,
        correctAnswers: calculatedResults.correctAnswers,
        wrongAnswers: calculatedResults.wrongAnswers,
        totalQuestions: calculatedResults.totalQuestions,
        answeredQuestions: calculatedResults.answeredQuestions,
        skippedQuestions: calculatedResults.skippedQuestions,
        timeSpent: calculatedResults.timeSpent,
        topics: selectedTopics.value
      }
      const submitted = await submitWithRetry(submissionData)
      if (submitted) {
        clearAssignedTestSession()
        await loadAssignedTests()
        clearTestState()
      }
    }
    
    showTrainingFinishDialog.value = true
    return
  }
  
  // Exam Mode: Save immediately and show results
  await saveAndShowResults(calculatedResults)
}

const retryTest = () => {
  // CRITICAL: Stop timer first
  stopTimer()
  
  // CRITICAL: Clear localStorage completely
  clearTestState()
  clearAssignedTestSession()
  
  // Reset ALL state variables to initial values
  testStarted.value = false
  showResults.value = false
  currentQuestionIndex.value = 0
  userAnswer.value = null
  answered.value = false
  userAnswers.value = []
  questionStates.value = {}
  timeLeft.value = 2400
  tests.value = []
  startTime.value = 0
  results.value = null
  pendingResults.value = null
  activeTestVersion.value = null
  
  // Reset video state
  videoError.value = false
  videoLoading.value = false
  
  // Reset dialog states
  showTrainingFinishDialog.value = false
  showCloseConfirm.value = false
  
  // Force component re-render
  nextTick(() => {
    // Scroll to top to show test configuration
    window.scrollTo({ top: 0, behavior: 'smooth' })
  })
}

// РўРµСЃС‚ РїСЂРѕРіСЂРµСЃСЃС–РЅ РЅУ©Р»РґРµСѓ - Р±Р°СЂР»С‹Т› С‚РµСЃС‚С‚РµСЂРґС– Т›Р°Р№С‚Р° С‚Р°РїСЃС‹СЂСѓ РјТЇРјРєС–РЅРґС–РіС–
const resetTestProgress = async () => {
  resetProgressLoading.value = true
  try {
    const response = await api.post('/tests/reset-progress')
    if (response.data.success) {
      snackbarStore.success(
        response.data.message || t('test.progressResetSuccess'),
        { timeout: 5000 }
      )
      showAllTestsCompletedDialog.value = false
      // Р–Р°ТЈР° С‚РµСЃС‚ Р±Р°СЃС‚Р°Сѓ
      await startTest()
    }
  } catch (error) {
    console.error('Error resetting progress:', error)
    snackbarStore.error(
      error.response?.data?.error || t('test.progressResetError'),
      { timeout: 5000 }
    )
  } finally {
    resetProgressLoading.value = false
  }
}

// Р”РёР°Р»РѕРіС‚С‹ Р¶Р°Р±Сѓ Р¶У™РЅРµ dashboard-Т›Р° У©С‚Сѓ
const closeAllTestsCompletedDialog = () => {
  showAllTestsCompletedDialog.value = false
  clearAssignedTestSession()
  router.push('/student')
}

const goToDashboard = () => {
  clearTestState()
  clearAssignedTestSession()
  router.push('/student')
}

// Training Mode functions
const saveAndShowResults = async (calculatedResults) => {
  results.value = calculatedResults
  
  // Save results to backend with detailed statistics
  if (tests.value && tests.value.length > 0) {
    const submissionData = {
      testIds: tests.value.map(t => t.id),
      answers: userAnswers.value,
      mode: selectedMode.value,
      assignedTestId: activeAssignedTestId.value,
      score: calculatedResults.score,
      correctAnswers: calculatedResults.correctAnswers,
      wrongAnswers: calculatedResults.wrongAnswers,
      totalQuestions: calculatedResults.totalQuestions,
      answeredQuestions: calculatedResults.answeredQuestions,
      skippedQuestions: calculatedResults.skippedQuestions,
      timeSpent: calculatedResults.timeSpent,
      topics: selectedTopics.value
    }
    
    const submitted = await submitWithRetry(submissionData)
    if (submitted) {
      clearAssignedTestSession()
      await loadAssignedTests()
      clearTestState()
      // Refresh mistake count from server so it reflects the just-submitted answers
      loadIncorrectQuestionsSummary()
    }
  }

  showResults.value = true
  testStarted.value = false
}

const saveTrainingResults = async () => {
  showTrainingFinishDialog.value = false
  await saveAndShowResults(pendingResults.value)
  pendingResults.value = null
}

const closeTrainingDialog = () => {
  showTrainingFinishDialog.value = false
  pendingResults.value = null
  clearTestState()
  clearAssignedTestSession()
  router.push('/student')
}

const resetTrainingSessionState = () => {
  stopTimer()
  clearTestState()

  testStarted.value = false
  showResults.value = false
  currentQuestionIndex.value = 0
  userAnswer.value = null
  answered.value = false
  userAnswers.value = []
  questionStates.value = {}
  timeLeft.value = 2400
  tests.value = []
  startTime.value = 0
  results.value = null
  pendingResults.value = null
  activeTestVersion.value = null
  showCloseConfirm.value = false
  videoError.value = false
  videoLoading.value = false
}

const restartTraining = () => {
  showTrainingFinishDialog.value = false
  resetTrainingSessionState()

  // Force re-render by clearing reactive state
  nextTick(() => {
    // Auto-start new test with same settings after state is cleared
    setTimeout(() => {
      startTest()
    }, 150)
  })
}

const returnToTestSystem = async () => {
  showTrainingFinishDialog.value = false
  resetTrainingSessionState()

  isExamMode.value = false
  isMistakeReviewMode.value = false
  selectedMode.value = 'TRAINING_MODE'
  selectedTopics.value = ['ALL_TOPICS']
  questionCount.value = 40
  showLessons.value = false
  selectedLessonCategory.value = null
  currentLessons.value = []
  currentLesson.value = null
  lessonVideoSrc.value = null

  await router.replace('/student/tests')
  await nextTick()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const discardTraining = () => {
  showTrainingFinishDialog.value = false
  pendingResults.value = null
  
  // Go back to dashboard without saving
  clearTestState()
  router.push('/student')
}

const performCloseTest = () => {
  // Stop timer first
  stopTimer()
  
  // Clear localStorage completely
  clearTestState()
  
  // Reset ALL state to initial values
  testStarted.value = false
  showResults.value = false
  currentQuestionIndex.value = 0
  userAnswer.value = null
  answered.value = false
  userAnswers.value = []
  questionStates.value = {}
  timeLeft.value = 2400
  tests.value = []
  selectedMode.value = 'TRAINING_MODE'
  startTime.value = 0
  results.value = null
  pendingResults.value = null
  activeTestVersion.value = null
}

const requestCloseTest = () => {
  if (!testStarted.value) {
    performCloseTest()
    return
  }
  showCloseConfirm.value = true
}

const confirmCloseTest = async () => {
  showCloseConfirm.value = false
  
  // If test has started and user has answered questions, show results
  if (testStarted.value && userAnswers.value.length > 0) {
    // Call finishTest to calculate and show results
    await finishTest()
  } else {
    // If no answers yet, just close
    performCloseTest()
  }
}

const cancelCloseTest = () => {
  showCloseConfirm.value = false
}

// Enhanced touch functionality with mobile controls visibility
const handleTouchStart = (e) => {
  if (!isMobile.value) return

  const target = e.target
  touchStartedInIndicatorBar.value = !!(
    target &&
    typeof target.closest === 'function' &&
    target.closest('.toolbar-left-section, .question-indicators')
  )
  touchStartedInQuestionArea.value = !!(
    target &&
    typeof target.closest === 'function' &&
    target.closest('.question-section, .question-content, .answer-options, .answer-card, .action-buttons-container')
  )

  touchStartX.value = e.changedTouches[0].screenX
  touchStartY.value = e.changedTouches[0].screenY
}

const handleTouchEnd = (e) => {
  if (!isMobile.value) return
  touchEndX.value = e.changedTouches[0].screenX
  touchEndY.value = e.changedTouches[0].screenY

  // Question indicators bar should only scroll horizontally, not switch questions
  if (touchStartedInIndicatorBar.value) {
    touchStartedInIndicatorBar.value = false
    return
  }

  // In question area, keep native vertical scroll and button interaction only
  if (touchStartedInQuestionArea.value) {
    touchStartedInQuestionArea.value = false
    return
  }

  handleSwipe()
}

const showMobileControlsTemporarily = () => {
  if (!isMobile.value) return
  
  showMobileControls.value = true
  
  // Clear existing timeout
  if (mobileControlsTimeout.value) {
    clearTimeout(mobileControlsTimeout.value)
  }
  
  // Hide controls after 5 seconds (increased from 3)
  mobileControlsTimeout.value = setTimeout(() => {
    showMobileControls.value = false
  }, 5000)
}

const lockPageScrollForMobileTest = () => {
  if (typeof window === 'undefined' || !isMobile.value) return
  const html = document.documentElement
  const body = document.body
  if (!html || !body) return
  // iOS Safari-РґР° overflow:hidden html/body-Т“Р° РѕСЂРЅР°С‚СЃР°, fixed СЌР»РµРјРµРЅС‚С‚РµСЂ С–С€С–РЅРґРµРіС–
  // scroll Р¶Т±РјС‹СЃ С–СЃС‚РµРјРµР№РґС–. РўРµРє overscroll-behavior РїР°Р№РґР°Р»Р°РЅР°РјС‹Р·.
  html.style.overscrollBehaviorY = 'none'
  body.style.overscrollBehaviorY = 'none'
  // window.innerHeight = Р±СЂР°СѓР·РµСЂ toolbar-РґР°РЅ РєРµР№С–РЅРіС– РЅР°Т›С‚С‹ viewport Р±РёС–РєС‚С–РіС–
  updateTestVhVar()
}

const unlockPageScrollForMobileTest = () => {
  if (typeof window === 'undefined') return
  const html = document.documentElement
  const body = document.body
  if (!html || !body) return
  html.style.overscrollBehaviorY = ''
  body.style.overscrollBehaviorY = ''
  document.documentElement.style.removeProperty('--test-vh')
  exitTestFullscreen()
}

// iOS/Android: РЅР°Т›С‚С‹ viewport Р±РёС–РєС‚С–РіС–РЅ CSS variable-Т“Р° Р¶Р°Р·
const updateTestVhVar = () => {
  if (typeof window === 'undefined') return
  document.documentElement.style.setProperty('--test-vh', `${window.innerHeight}px`)
}

// Fullscreen API (Android Chrome / modern browsers; iOS-С‚Р° Р¶Т±РјС‹СЃ Р¶Р°СЃР°РјР°Р№РґС‹)
const enterTestFullscreen = async () => {
  if (typeof window === 'undefined') return
  updateTestVhVar()
    // Fullscreen-С‚Р°РЅ РєРµР№С–РЅ РЅР°Т›С‚С‹ Р±РёС–РєС‚С–РєС‚С– Р¶Р°ТЈР°СЂС‚
    // Р‘СЂР°СѓР·РµСЂ Р¶Р°С‚Т›С‹Р·Р±Р°РґС‹ вЂ” fallback СЂРµС‚С–РЅРґРµ --test-vh Р¶РµС‚РєС–Р»С–РєС‚С–
}

const exitTestFullscreen = async () => {
  if (typeof window === 'undefined') return
  updateTestVhVar()
}

const handleSwipe = () => {
  const swipeThreshold = 50
  const diffX = touchStartX.value - touchEndX.value
  const diffY = touchStartY.value - touchEndY.value
  
  // Vertical swipe вЂ” don't interfere with native scroll
  if (Math.abs(diffY) > Math.abs(diffX)) {
    return
  }
  
  // Horizontal swipe for navigation
  if (Math.abs(diffX) > swipeThreshold) {
    showMobileControlsTemporarily()
    
    if (diffX > 0) {
      // Swipe left - next question (no need to answer first)
      if (currentQuestionIndex.value < (tests.value?.length || 0) - 1) {
        nextQuestion()
      } else if (currentQuestionIndex.value === (tests.value?.length || 0) - 1) {
        finishTest()
      }
    } else {
      // Swipe right - previous question (always allowed)
      if (currentQuestionIndex.value > 0) {
        previousQuestion()
      }
    }
  }
}

// Load available topics
const loadTopics = async () => {
  // Temporarily disable all animations to prevent NaN errors
  const originalAnimations = document.documentElement.style.getPropertyValue('--vuetify-animations')
  document.documentElement.style.setProperty('--vuetify-animations', 'none')
  
  // Add CSS to completely disable animations during loading
  const disableAnimationsStyle = document.createElement('style')
  disableAnimationsStyle.id = 'disable-animations-temp'
  disableAnimationsStyle.textContent = `
    .v-application *,
    .v-select *, 
    .v-list *, 
    .v-menu *, 
    .v-overlay *,
    .v-enter-active, 
    .v-leave-active,
    .v-enter-from,
    .v-enter-to, 
    .v-leave-from,
    .v-leave-to {
      animation: none !important;
      animation-duration: 0s !important;
      animation-delay: 0s !important;
      transition: none !important;
      transition-duration: 0s !important;
      transition-delay: 0s !important;
      will-change: auto !important;
    }
    .v-overlay__scrim {
      transition: none !important;
    }
  `
  document.head.appendChild(disableAnimationsStyle)
  
  try {
    topicsLoading.value = true
    
    // Use only public topics endpoint
    const response = await api.get('/topics')
    
    if (response.data && response.data.topics) {
      // Defensive update to prevent animation calculation errors
      const topics = Array.isArray(response.data.topics) ? response.data.topics : []
      
      // Wait extra time for CSS animation blocking to take full effect
      await new Promise(resolve => setTimeout(resolve, 100))
      await new Promise(resolve => requestAnimationFrame(resolve))
      await nextTick()
      
      // Now perform assignments with animation blocking in full effect
      availableTopics.value = [...topics]
      
      await nextTick()
      await new Promise(resolve => setTimeout(resolve, 50))
      
      // Select "All" by default if none selected  
      if (!selectedTopics.value || selectedTopics.value.length === 0) {
        selectedTopics.value = ['ALL_TOPICS']
      }
      
      await nextTick()
      await new Promise(resolve => setTimeout(resolve, 50))
    } else {
      throw new Error('Invalid response format')
    }
  } catch (error) {
    // Failed to load topics
    availableTopics.value = []
  } finally {
    topicsLoading.value = false
    
    // Ensure selected category is valid; if not, default to 'all'
    try {
      const validSet = new Set(availableTopics.value || [])
      if (!validSet.has(selectedCategory.value)) {
        selectedCategory.value = 'all'
      }
    } catch (e) {
      selectedCategory.value = 'all'
    }
    
    // Re-enable animations after extended delay to ensure all reactive updates complete
    setTimeout(() => {
      const tempStyle = document.getElementById('disable-animations-temp')
      if (tempStyle) {
        tempStyle.remove()
      }
      document.documentElement.style.setProperty('--vuetify-animations', originalAnimations)
    }, 1000)
  }
}

// Video protection - prevent URL copying and DevTools
const preventDevTools = (e) => {
  // Block F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
  if (
    e.keyCode === 123 || // F12
    (e.ctrlKey && e.shiftKey && e.keyCode === 73) || // Ctrl+Shift+I
    (e.ctrlKey && e.shiftKey && e.keyCode === 74) || // Ctrl+Shift+J
    (e.ctrlKey && e.keyCode === 85) // Ctrl+U (view source)
  ) {
    e.preventDefault()
    return false
  }
}

// Watch isExamMode and automatically set question count to 40 and select all topics
watch(isExamMode, (newValue) => {
  if (newValue) {
    // Exam mode requires exactly 40 questions from all topics
    questionCount.value = 40
    // Р‘Р°СЂР»С‹Т› С‚Р°Т›С‹СЂС‹РїС‚С‹ С‚Р°ТЈРґР°Сѓ
    selectedTopics.value = ['ALL_TOPICS']
  }
})

// Lifecycle
onMounted(async () => {
  // Flush any pending offline submissions
  flushSubmitQueue()
  
  // Listen for network reconnection to flush queue
  window.addEventListener('online', flushSubmitQueue)
  
  // Check if user is LIMITED_STUDENT and block access
  if (isLimitedStudent.value) {
    // Show error message and redirect after delay
    snackbarStore.error(
      t('student.restrictions.accessDenied'),
      { timeout: 5000 }
    )
    setTimeout(() => {
      router.push('/student/dashboard')
    }, 2000)
    return
  }

  // Add keyboard protection
  document.addEventListener('keydown', preventDevTools)

  // Load topics first with error handling
  try {
    // Add a small delay to prevent race conditions with DOM animations
    await new Promise(resolve => setTimeout(resolve, 100))
    await loadTopics()
  } catch (error) {
    // Set fallback values to prevent undefined errors
    availableTopics.value = Object.freeze([])
    selectedTopics.value = Object.freeze(['ALL_TOPICS'])
  }
  
  try {
    const restored = await restoreTestState()
    if (restored) {
      autoStarting.value = false
      return
    }
  } catch (error) {
    // Failed to restore test state
  }

  await Promise.all([
    loadIncorrectQuestionsSummary(),
    loadAssignedTests()
  ])

  // Check for mode parameter in URL
  const urlParams = new URLSearchParams(window.location.search)
  const modeParam = urlParams.get('mode')
  const topicsParam = urlParams.get('topics')
  const mistakesParam = urlParams.get('mistakes')
  const assignedParam = urlParams.get('assigned')
  const shouldPrefillMistakeReview = mistakesParam === '1' || mistakesParam === 'true'
  const shouldPrefillAssigned = assignedParam && !Number.isNaN(Number(assignedParam))
  
  if (modeParam) {
    selectedMode.value = modeParam
    
    // Auto-start test if mode is provided
    if (modeParam === 'EXAM' || modeParam === 'QUESTIONS_40') {
      autoStarting.value = true
      setTimeout(() => {
        startTest()
        autoStarting.value = false
      }, 1500) // Small delay to show loading
    }
  }

  if (shouldPrefillMistakeReview) {
    isMistakeReviewMode.value = true
    selectedMode.value = 'TRAINING_MODE'
    isExamMode.value = false
    selectedTopics.value = ['ALL_TOPICS']
  }

  if (shouldPrefillAssigned) {
    const requestedAssignmentId = Number(assignedParam)
    if (assignedTests.value.some(item => item.id === requestedAssignmentId)) {
      selectedAssignedTestId.value = requestedAssignmentId
      isAssignedTestMode.value = true
      selectedMode.value = 'TRAINING_MODE'
      isExamMode.value = false
      selectedTopics.value = ['ALL_TOPICS']
    }
  }

  // Pre-select topics from URL (e.g. from recommended tests)
  if (topicsParam && !shouldPrefillMistakeReview) {
    const requestedTopics = decodeURIComponent(topicsParam).split(',')
    const available = availableTopics.value || []
    const matched = requestedTopics.filter(rt => available.includes(rt))
    if (matched.length > 0) {
      selectedTopics.value = matched
    }
  }
  
  // Initialize mobile controls behavior
  if (isMobile.value) {
    // Hide controls initially on mobile - they will show only on swipe/touch
    showMobileControls.value = false
  }
})

// Debug function for mobile layout issues
const debugMobileLayout = () => {
  const isMobile = window.innerWidth <= 768;
  
  if (isMobile) {
    // Additional mobile-specific debugging
    setTimeout(() => {
    }, 1000);
  }
};

// Helper function to check if element is visible
const isElementVisible = (element) => {
  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    style.opacity !== '0'
  );
};

onUnmounted(() => {
  stopTimer()
  
  // Remove online listener
  window.removeEventListener('online', flushSubmitQueue)
  window.removeEventListener('resize', updateTestVhVar)
  
  // Clear mobile controls timeout
  if (mobileControlsTimeout.value) {
    clearTimeout(mobileControlsTimeout.value)
  }

  // Remove keyboard protection
  document.removeEventListener('keydown', preventDevTools)

  // Ensure page scroll is restored
  unlockPageScrollForMobileTest()
})

watch(
  () => testStarted.value && !showResults.value,
  (isActive) => {
    if (isActive) {
      lockPageScrollForMobileTest()
      window.addEventListener('resize', updateTestVhVar)
    } else {
      unlockPageScrollForMobileTest()
      window.removeEventListener('resize', updateTestVhVar)
    }
  },
  { immediate: true }
)

// Watch for topic selection changes with error handling
watch(selectedTopics, (newTopics, oldTopics) => {
  try {
    if (!newTopics || newTopics.length === 0) {
      // If nothing selected, select "All" by default
      selectedTopics.value = Object.freeze(['ALL_TOPICS'])
      return
    }
    
    const hasAll = Array.isArray(newTopics) && newTopics.includes('ALL_TOPICS')
    const hadAll = Array.isArray(oldTopics) && oldTopics?.includes('ALL_TOPICS')
    
    // If "All" was just added and there are other topics, remove other topics
    if (hasAll && newTopics.length > 1) {
      if (!hadAll) {
        // User just selected "All", remove all other selections
        selectedTopics.value = Object.freeze(['ALL_TOPICS'])
      } else {
        // User selected another topic while "All" was selected, remove "All"
        const filtered = newTopics.filter(t => t !== 'ALL_TOPICS')
        selectedTopics.value = Object.freeze(filtered)
      }
    }
  } catch (error) {
    // Error in selectedTopics watcher
    selectedTopics.value = Object.freeze(['ALL_TOPICS'])
  }
}, { 
  immediate: false,
  flush: 'sync' // Use synchronous flushing to prevent timing issues
})

// Watch for video changes
watch(currentVideo, () => {
  testVideoSrc.value = currentVideo.value || null
  videoError.value = false
  videoLoading.value = true
  if (isMobile.value && pendingMobileVideoAlign.value) {
    scheduleMobileVideoAlign()
  }
  // <source> элементін қолданғандықтан, .load() шақыру керек (iOS Safari үшін маңызды)
  nextTick(() => {
    if (videoPlayer.value) {
      videoPlayer.value.load()
      safePlay(videoPlayer.value)
    }
  })
  setTimeout(() => {
    videoLoading.value = false
  }, 500)
}, { immediate: true })
// Watch for Training Finish Dialog closure
watch(showTrainingFinishDialog, (newVal, oldVal) => {
  // If dialog was closed without clicking any button (e.g., by clicking outside or ESC)
  // Results are already auto-saved, so just clean up
  if (oldVal === true && newVal === false && pendingResults.value !== null) {
    pendingResults.value = null
    testStarted.value = false
    showResults.value = false
    currentQuestionIndex.value = 0
    userAnswer.value = null
    answered.value = false
    userAnswers.value = []
    tests.value = []
  }
})

// РњРѕР±РёР»СЊРґС–: СЃТ±СЂР°Т› У©Р·РіРµСЂРіРµРЅ РєРµР·РґРµ СЃРєСЂРѕР»Р»РґС‹ Р¶РѕТ“Р°СЂС‹Т“Р° Р°РІС‚РѕРјР°С‚С‚С‹ Т›Р°Р№С‚Р°СЂСѓ
watch(currentQuestionIndex, () => {
  if (!isMobile.value) return
  nextTick(() => {
    blurActiveControl()
    scheduleMobileVideoAlign()
  })
}, { flush: 'post' })
</script>

<style scoped>
.test-system {
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  /* Prevent animation calculation issues */
  backface-visibility: hidden;
  transform-style: preserve-3d;
}

/* Fullscreen mode when test is active */
.test-system.test-active {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background: #1a1a1a !important;
  overflow: hidden !important;
}

/* Hide any potential background elements */
.test-system.test-active::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #1a1a1a;
  z-index: -1;
}

.mode-selection-card {
  max-width: 900px;
  margin: 0 auto;
  background: rgba(45, 45, 45, 0.9);
  border: 1px solid rgba(255, 193, 7, 0.2);
}

.mode-selection-container {
  padding-top: 24px !important;
  padding-bottom: 24px !important;
}

.mode-selection-title {
  font-size: clamp(1.95rem, 3.2vw, 2.7rem);
  line-height: 1.08;
  margin-bottom: 1.2rem !important;
}

.mode-selection-card > .v-row.mb-4 {
  margin-bottom: 0.85rem !important;
}

.mode-selection-card > .v-row.mb-5 {
  margin-bottom: 1rem !important;
}

.mode-selection-card .v-col {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.mode-selection-card .v-field {
  min-height: 52px;
}

.mode-selection-card .v-field__input {
  min-height: 52px !important;
}

.mode-selection-card .v-btn-toggle .v-btn {
  min-height: 46px;
}

.mode-selection-start-bar {
  margin-top: 12px;
  position: relative;
  z-index: 1;
}

.restriction-card {
  background: rgba(45, 45, 45, 0.95) !important;
  border: 2px solid rgba(255, 152, 0, 0.3) !important;
}

.training-card {
  background: rgba(33, 150, 243, 0.1) !important;
  border-radius: 16px !important;
  transition: all 0.3s ease !important;
  padding: 20px !important;
}

.training-card:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px rgba(33, 150, 243, 0.2) !important;
}

.training-submode-label {
  margin-bottom: 10px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
}

.training-submode-toggle {
  width: 100%;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(255, 193, 7, 0.16);
}

.training-submode-toggle .v-btn {
  min-height: 48px;
  text-transform: none;
  font-weight: 700;
}

.mistake-review-card {
  background: rgba(255, 193, 7, 0.1) !important;
  border: 1px solid rgba(255, 193, 7, 0.22) !important;
  border-radius: 16px !important;
}

.mistake-review-kicker {
  color: #ffc107;
  letter-spacing: 0.08em;
}

.mistake-count-toggle {
  width: 100%;
}

.mistake-count-section {
  margin-bottom: 8px;
}

.mistake-count-toggle .v-btn {
  min-height: 44px;
  text-transform: none;
  font-weight: 700;
}

.compact-mode-card-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 52px;
}

.compact-mode-card-icon {
  margin-bottom: 0 !important;
  flex-shrink: 0;
}

.compact-mode-card-title {
  font-size: 1rem !important;
  line-height: 1.15 !important;
}

.compact-mistake-review-title {
  line-height: 1.15 !important;
}

.compact-mistake-review-text {
  max-width: 100%;
}

.mode-selection-start-btn {
  min-height: 56px !important;
  font-size: 1rem !important;
  letter-spacing: 0.01em !important;
}

.mode-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  height: 100%;
}

.mode-card:hover {
  transform: translateY(-4px);
  border-color: rgba(255, 193, 7, 0.5);
}

.mode-card.selected {
  border-color: #FFC107;
  background: rgba(255, 193, 7, 0.1);
}

.test-interface {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.test-interface.fullscreen {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 99999 !important;
  background: #1a1a1a !important;
}

.test-topbar-container {
  position: sticky;
  top: 0;
  background: rgba(30, 30, 30, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 193, 7, 0.2);
  z-index: 1001;
}

.test-toolbar-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  height: auto;
  min-height: 64px;
  gap: 12px;
  flex-wrap: nowrap;
}

/* Mobile toolbar optimizations */
@media (max-width: 768px) {
  .test-toolbar-content {
    padding: 6px 8px !important;
    min-height: 52px !important;
    gap: 8px !important;
  }
  
  .test-topbar-container {
    backdrop-filter: blur(8px) !important;
  }
}

.toolbar-left-section {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0; /* Allows flex shrinking */
}

/* Legacy toolbar right section removed - now using desktop-specific and mobile floating */

/* Mobile Top Actions Bar */
.mobile-top-actions {
  display: none; /* Hidden on desktop by default */
  justify-content: space-between;
  align-items: center;
  padding: 2px 6px;
  background: rgba(26, 26, 26, 0.95);
  border-bottom: 1px solid rgba(255, 193, 7, 0.2);
  gap: 6px;
  min-height: 32px;
}

.mobile-lang-buttons {
  display: flex;
  gap: 2px;
  align-items: center;
}

.mobile-lang-toggle {
  min-width: 28px !important;
  height: 24px !important;
  padding: 0 6px !important;
  font-size: 0.7rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
}

.mobile-timer {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.mobile-theme-switcher {
  margin-left: auto;
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  min-height: 28px !important;
  flex-shrink: 0;
}

.mobile-theme-switcher .v-icon {
  font-size: 16px !important;
}

.mobile-close-btn {
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  min-height: 24px !important;
  flex-shrink: 0;
}

/* Question Indicators */
.question-indicators {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 6px 0;
  align-items: center;
  justify-content: flex-start;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 193, 7, 0.5) transparent;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

/* Webkit scrollbar styling */
.question-indicators::-webkit-scrollbar {
  height: 4px;
}

.question-indicators::-webkit-scrollbar-track {
  background: transparent;
}

.question-indicators::-webkit-scrollbar-thumb {
  background: rgba(255, 193, 7, 0.5);
  border-radius: 2px;
}

.question-indicators::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 193, 7, 0.7);
}

.question-indicator {
  min-width: 36px;
  max-width: 36px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  border: 2px solid transparent;
  user-select: none;
  box-sizing: border-box;
}

.question-indicator:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Prevent transform NaN issues */
.question-indicator * {
  transform-origin: center center;
  backface-visibility: hidden;
}

/* Prevent animation issues during DOM updates */
.test-system * {
  will-change: auto;
}

.test-system .v-btn {
  transform-origin: center center;
}

/* Fix potential Vuetify animation issues */
.test-system .v-btn:hover {
  transform: none !important;
}

.test-system .v-progress-linear {
  transform: none !important;
}

/* РњРђТўР«Р—Р”Р«: <video> СЌР»РµРјРµРЅС‚С–РЅРµ transform С‚С–СЂРєРµСѓ iOS Safari-РґР° РІРёРґРµРѕРЅС‹ Р¶Р°СЃС‹СЂР°РґС‹.
   Р‘Р°СЂР»С‹Т› * СЌР»РµРјРµРЅС‚С‚РµСЂРіРµ transform:translateZ(0) С‚С–СЂРєРµСѓ ТљРђРЈР†РџРўР† вЂ” Р¶РѕСЋРґС‹ТЈ РѕСЂРЅС‹РЅР°
   С‚РµРє РЅР°Т›С‚С‹ РїСЂРѕР±Р»РµРјР° С‚СѓРґС‹СЂР°С‚С‹РЅ СЌР»РµРјРµРЅС‚С‚РµСЂРіРµ (v-btn) Т“Р°РЅР° Т›РѕР»РґР°РЅР°РјС‹Р·. */

/* Prevent any NaN values in transforms вЂ” С‚РµРє РєРЅРѕРїРєР°Р»Р°СЂ РјРµРЅ РёРЅРґРёРєР°С‚РѕСЂР»Р°СЂТ“Р° */
.test-system .v-btn,
.test-system .question-indicator {
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
}

/* Video СЌР»РµРјРµРЅС‚С– РµС€Т›Р°С€Р°РЅ transform Р°Р»РјР°СѓС‹ С‚РёС–СЃ вЂ” iOS Safari native renderer */
.test-video,
.video-container,
.video-section {
  transform: none !important;
  -webkit-transform: none !important;
  will-change: auto !important;
}

/* Override Vuetify animations that might cause NaN */
.test-system .v-enter-active,
.test-system .v-leave-active,
.test-system .v-move {
  transition: opacity 0.2s ease !important;
  transform: none !important;
}

.test-system .v-enter-from,
.test-system .v-enter-to,
.test-system .v-leave-from,
.test-system .v-leave-to {
  transform: none !important;
}

/* Disable problematic Vue transitions */
.test-system .v-btn,
.test-system .v-card,
.test-system .v-chip {
  transition-property: opacity, color, background-color, border-color !important;
}

/* Force safe transform values вЂ” С‚РµРє РєРЅРѕРїРєР°Р»Р°СЂ РјРµРЅ UI СЌР»РµРјРµРЅС‚С‚РµСЂС– ТЇС€С–РЅ, video РµРјРµСЃ */
.test-system .v-btn[style*="transform"] {
  transform: translateZ(0) !important;
}

.test-system .v-btn[style*="scale"] {
  transform: scale(1) translateZ(0) !important;
}

.question-indicator.correct {
  background: rgba(76, 175, 80, 0.3);
  border-color: #4CAF50;
  color: #4CAF50;
}

.question-indicator.wrong {
  background: rgba(244, 67, 54, 0.3);
  border-color: #F44336;
  color: #F44336;
}

.question-indicator.skipped {
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
}

/* Current indicator always visible - overrides other states for visibility */
.question-indicator.current,
.question-indicator.skipped.current {
  background: rgba(33, 150, 243, 0.3) !important;
  border-color: #2196F3 !important;
  color: #2196F3 !important;
  box-shadow: 0 0 12px rgba(33, 150, 243, 0.5) !important;
}

/* Mobile styles for question indicators - smaller and perfectly round */
@media (max-width: 768px) {
  .question-indicator {
    min-width: 28px !important;
    max-width: 28px !important;
    width: 28px !important;
    height: 28px !important;
    font-size: 0.7rem !important;
    border-radius: 50% !important;
    border-width: 1.5px !important;
  }
  
  .question-indicators {
    padding: 4px 0 !important;
    gap: 4px !important;
  }
}

.toolbar-title {
  flex-shrink: 0;
}

.toolbar-spacer {
  flex-grow: 0;
}

/* Language Selector */
/* Legacy language selector (keep for compatibility) */
.language-selector {
  margin-left: auto;
  position: relative;
  flex-shrink: 0;
}

/* New compact round language selector */
.language-selector-compact {
  position: relative;
  flex-shrink: 0;
  margin-right: 8px; /* Small gap before timer/close button */
}

.language-btn-round {
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  border-radius: 50% !important;
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  color: rgba(255, 255, 255, 0.9) !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
  background: rgba(0, 0, 0, 0.3) !important;
  backdrop-filter: blur(6px) !important;
  transition: all 0.2s ease !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* Mobile compact buttons */
@media (max-width: 768px) {
  .language-btn-round.mobile-compact {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    font-size: 0.6rem !important;
  }
  
  .close-test-btn-toolbar.mobile-compact {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
  }
  
  .language-selector-compact {
    margin-right: 4px !important;
  }
}

.language-btn-round:hover {
  background: rgba(255, 193, 7, 0.2) !important;
  border-color: rgba(255, 193, 7, 0.6) !important;
  color: rgba(255, 255, 255, 1) !important;
}

.language-btn {
  height: 36px !important;
  min-width: 48px !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  color: rgba(255, 255, 255, 0.8) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
  background: rgba(0, 0, 0, 0.2) !important;
  backdrop-filter: blur(4px) !important;
  transition: all 0.2s ease !important;
  padding: 0 8px !important;
}

.language-btn:hover {
  background: rgba(255, 193, 7, 0.15) !important;
  border-color: rgba(255, 193, 7, 0.4) !important;
  color: rgba(255, 255, 255, 1) !important;
}

.language-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 70px;
  background: rgba(30, 30, 30, 0.9);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  z-index: 1002;
  margin-top: 2px;
  overflow: hidden;
}

/* Compact dropdown for round language selector */
.language-dropdown-compact {
  position: absolute;
  top: 0;
  left: 100%;
  display: flex;
  flex-direction: row;
  min-width: auto;
  background: rgba(20, 20, 20, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.6);
  z-index: 1002;
  margin-left: 8px;
  overflow: hidden;
  animation: fadeInScale 0.15s ease-out;
}

.language-item {
  padding: 6px 10px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.7rem;
  font-weight: 500;
  transition: all 0.15s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: transparent;
}

/* Compact language items for round selector */
.language-item-compact {
  padding: 8px 10px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.65rem;
  font-weight: 600;
  text-align: center;
  transition: all 0.2s ease;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  background: transparent;
  white-space: nowrap;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.language-item-compact:last-child {
  border-right: none;
}

.language-item-compact:hover {
  background: rgba(255, 193, 7, 0.12);
  color: rgba(255, 193, 7, 0.95);
  transform: scale(1.05);
}

.language-item-compact.active {
  background: rgba(255, 193, 7, 0.2);
  color: rgba(255, 193, 7, 1);
  font-weight: 700;
}

.language-item:last-child {
  border-bottom: none;
}

.language-item:hover {
  background: rgba(255, 193, 7, 0.08);
  color: rgba(255, 193, 7, 0.9);
}

.language-item.active {
  background: rgba(255, 193, 7, 0.15);
  color: rgba(255, 193, 7, 1);
  font-weight: 600;
}

.language-menu {
  background: transparent !important;
}

.language-menu .v-list-item {
  min-height: 36px !important;
  padding: 6px 12px !important;
  color: rgba(255, 255, 255, 0.9) !important;
  transition: all 0.2s ease;
}

.language-menu .v-list-item:hover {
  background: rgba(255, 193, 7, 0.1) !important;
  color: #FFC107 !important;
}

.language-menu .v-list-item--active {
  background: rgba(255, 193, 7, 0.2) !important;
  color: #FFC107 !important;
  font-weight: 600;
}

.language-select .v-field__input {
  color: white !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  padding: 0 8px !important;
  min-height: 40px !important;
}

.language-select .v-field__outline {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.language-select .v-field__outline--focused {
  border-color: #FFC107 !important;
}

.language-selected {
  color: white !important;
  font-size: 12px !important;
  font-weight: 600 !important;
}

.language-item {
  color: white !important;
}

.language-item:hover {
  background: rgba(255, 193, 7, 0.1) !important;
}

.close-test-btn {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 10001;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.close-test-btn-toolbar {
  margin-left: 12px !important;
  flex-shrink: 0 !important;
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  min-height: 36px !important;
}

.test-topbar {
  flex-shrink: 0;
}

.timer {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: rgba(255, 193, 7, 0.1);
  border-radius: 8px;
  color: #ffffff;
}

.timer .text-h6 {
  color: #ffffff;
  font-weight: 600;
}

.timer .text-error {
  color: #F44336 !important;
}

/* Mobile timer styles */
@media (max-width: 768px) {
  .timer.mobile-timer {
    padding: 4px 8px !important;
    border-radius: 16px !important;
  }
  
  .timer-text {
    font-size: 0.8rem !important;
    font-weight: 600 !important;
  }
}

.test-content {
  flex: 1;
  overflow: hidden;
  height: auto;
  display: flex;
}

.test-row {
  height: 100%;
  margin: 0;
}

.video-section {
  background: transparent;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  position: relative;
  padding: 0;
  height: fit-content;
  min-height: 0;
  flex-shrink: 0;
  max-width: 100%;
}

.video-container {
  width: 100%;
  max-width: 100%;
  height: fit-content;
  position: relative;
  display: block;
  overflow: hidden;
}

/* Video Restart Button */
.video-restart-button {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 100;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  transition: all 0.3s ease;
  cursor: pointer;
}

.video-restart-button:hover {
  background: rgba(0, 0, 0, 0.9);
  transform: scale(1.1);
}

.restart-btn {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
}

.test-video {
  width: 100%;
  max-width: 100%;
  height: auto;
  max-height: 75vh;
  object-fit: contain;
  border-radius: 0;
  display: block;
  background: #000;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* Hide all video controls including play button */
.test-video::-webkit-media-controls,
.test-video::-webkit-media-controls-panel,
.test-video::-webkit-media-controls-play-button,
.test-video::-webkit-media-controls-timeline,
.test-video::-webkit-media-controls-current-time-display,
.test-video::-webkit-media-controls-time-remaining-display,
.test-video::-webkit-media-controls-volume-slider,
.test-video::-webkit-media-controls-mute-button,
.test-video::-webkit-media-controls-fullscreen-button {
  display: none !important;
}

/* Firefox - hide all controls */
.test-video::-moz-media-controls,
.test-video::-moz-media-controls-panel,
.test-video::-moz-media-controls-play-button,
.test-video::-moz-media-controls-timeline,
.test-video::-moz-media-controls-current-time-display,
.test-video::-moz-media-controls-time-remaining-display {
  display: none !important;
}

.video-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  background: rgba(0, 0, 0, 0.8);
  padding: 20px;
  border-radius: 8px;
}

.question-section {
  background: #1a1a1a;
  overflow-y: auto;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  max-width: 100%;
}

.question-content {
  max-width: 800px;
  margin: 0 auto;
}

.question-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.question-meta-chip {
  max-width: 100%;
}

.question-version-chip {
  font-weight: 600;
}

.question-text {
  color: #fff;
  line-height: 1.6;
}

.answer-card {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  display: block;
  padding: 0;
  margin: 0;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease !important;
  border: 2px solid transparent !important;
  background: rgba(45, 45, 45, 0.8) !important;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.answer-option-indicator {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.65);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.answer-option-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: transparent;
  transform: scale(0);
  transition: transform 0.2s ease, background 0.2s ease;
}

.answer-option-indicator.is-selected {
  border-color: #FFC107;
}

.answer-option-indicator.is-selected .answer-option-dot {
  background: #FFC107;
  transform: scale(1);
}

.answer-option-indicator.is-correct {
  border-color: #4CAF50;
}

.answer-option-indicator.is-correct .answer-option-dot {
  background: #4CAF50;
  transform: scale(1);
}

.answer-option-indicator.is-incorrect {
  border-color: #F44336;
}

.answer-option-indicator.is-incorrect .answer-option-dot {
  background: #F44336;
  transform: scale(1);
}

.answer-card:hover {
  border-color: rgba(255, 193, 7, 0.5) !important;
  background: rgba(255, 193, 7, 0.05) !important;
}

.answer-card:focus-visible {
  outline: 2px solid #FFC107;
  outline-offset: 2px;
}

.v-card.answer-card.selected,
.answer-card.v-card.selected,
.answer-card.selected {
  border-color: #FFC107 !important;
  background-color: rgba(255, 193, 7, 0.15) !important;
  background: rgba(255, 193, 7, 0.15) !important;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.2) !important;
}

.v-card.answer-card.correct,
.answer-card.v-card.correct,
.answer-card.correct {
  border-color: #4CAF50 !important;
  background-color: rgba(76, 175, 80, 0.25) !important;
  background: rgba(76, 175, 80, 0.25) !important;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2) !important;
}

.v-card.answer-card.incorrect,
.answer-card.v-card.incorrect,
.answer-card.incorrect {
  border-color: #F44336 !important;
  background-color: rgba(244, 67, 54, 0.25) !important;
  background: rgba(244, 67, 54, 0.25) !important;
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.2) !important;
}

.results-card {
  max-width: 900px;
  margin: 0 auto;
  background: rgba(45, 45, 45, 0.9);
}

.stat-card {
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: scale(1.05);
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .mode-selection-container {
    padding-top: 12px !important;
    padding-bottom: 16px !important;
  }

  body, html {
    overflow-x: hidden !important;
    max-width: 100vw !important;
  }

  /* Mode Selection Card Mobile */
  .mode-selection-card {
    max-width: 100% !important;
    margin: 0 6px !important;
    padding: 12px !important;
    padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px)) !important;
    border-radius: 16px !important;
    overflow: visible !important;
  }

  .mode-selection-title {
    font-size: 1.35rem !important;
    line-height: 1.08 !important;
    margin-bottom: 0.85rem !important;
  }

  .mode-selection-card > .v-row.mb-4 {
    margin-bottom: 0.55rem !important;
  }

  .mode-selection-card > .v-row.mb-5 {
    margin-bottom: 0.7rem !important;
  }

  .mode-selection-card .v-col {
    padding-top: 5px !important;
    padding-bottom: 5px !important;
  }

  .mode-selection-card .v-field {
    min-height: 46px !important;
  }

  .mode-selection-card .v-field__input {
    min-height: 46px !important;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
  }

  .mode-selection-card .text-caption,
  .mode-selection-card .v-messages {
    font-size: 0.72rem !important;
    line-height: 1.25 !important;
  }

  .training-submode-label {
    margin-bottom: 6px !important;
    font-size: 0.68rem !important;
  }

  .training-submode-toggle .v-btn {
    min-height: 40px !important;
    font-size: 0.74rem !important;
    padding-inline: 6px !important;
  }

  .training-submode-toggle .v-btn .v-icon {
    display: none !important;
  }

  .training-card {
    padding: 12px !important;
    border-radius: 12px !important;
    margin-bottom: 0.6rem !important;
  }

  .mistake-review-card {
    padding: 12px !important;
    border-radius: 12px !important;
    margin-bottom: 0.6rem !important;
  }

  .mistake-review-card .text-h6 {
    font-size: 0.95rem !important;
    line-height: 1.2 !important;
  }

  .compact-mode-card-content {
    min-height: 36px !important;
    gap: 8px !important;
  }

  .compact-mode-card-title {
    font-size: 0.92rem !important;
  }

  .compact-mistake-review-header {
    gap: 10px !important;
  }

  .compact-mistake-review-title {
    font-size: 0.92rem !important;
    line-height: 1.15 !important;
  }

  .compact-mistake-review-text {
    font-size: 0.74rem !important;
    line-height: 1.22 !important;
    margin-top: 2px !important;
  }

  .mistake-review-card .v-chip {
    height: 30px !important;
    min-height: 30px !important;
    font-size: 0.76rem !important;
    padding-inline: 10px !important;
  }

  .mistake-count-toggle .v-btn {
    min-height: 36px !important;
    font-size: 0.78rem !important;
    padding-inline: 8px !important;
  }

  .mistake-count-section-mobile {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    margin-bottom: 6px !important;
  }

  .mistake-count-label-mobile {
    flex: 0 0 auto !important;
    font-size: 0.72rem !important;
    line-height: 1.1 !important;
    white-space: nowrap !important;
  }

  .mistake-count-toggle-mobile {
    flex: 1 1 auto !important;
    min-width: 0 !important;
  }

  .mistake-count-toggle-mobile .v-btn {
    min-height: 34px !important;
    font-size: 0.74rem !important;
    padding-inline: 6px !important;
  }

  .training-card .v-icon {
    font-size: 34px !important;
    margin-bottom: 4px !important;
  }

  .training-card .text-h5 {
    font-size: 0.95rem !important;
    margin-bottom: 2px !important;
    line-height: 1.2 !important;
  }

  /* Unified exam mode toggle styling */
  .training-card .v-switch {
    margin: 0 !important;
  }

  .training-card .v-switch .v-selection-control {
    margin: 0 !important;
  }

  .training-card .text-caption {
    font-size: 0.66rem !important;
    opacity: 0.8 !important;
  }

  /* Mobile exam mode toggle */
  .training-card .v-col-12 {
    padding: 2px !important;
  }

  .training-card .text-body-1 {
    font-size: 0.78rem !important;
    line-height: 1.15 !important;
  }

  .training-card .text-body-2 {
    display: none !important;
  }

  .training-card .mb-4 {
    margin-bottom: 0.35rem !important;
  }

  .mode-selection-start-bar {
    position: relative !important;
    inset: auto !important;
    z-index: 1 !important;
    margin: 12px 0 0 !important;
    padding: 0 !important;
    background: none !important;
    backdrop-filter: none !important;
    box-shadow: none !important;
    border: 0 !important;
    overflow: visible !important;
  }

  .mode-selection-start-bar-inline {
    position: relative !important;
    inset: auto !important;
    z-index: 1 !important;
    margin: 12px 0 0 !important;
    padding: 0 !important;
    background: none !important;
    backdrop-filter: none !important;
    box-shadow: none !important;
    border: 0 !important;
    overflow: visible !important;
  }

  .mode-selection-start-btn {
    min-height: 46px !important;
    height: 46px !important;
    font-size: 0.9rem !important;
    margin: 0 !important;
    position: relative !important;
    z-index: 1 !important;
  }

  .mode-selection-start-btn .v-icon {
    font-size: 1rem !important;
  }

  /* Mobile Toolbar */
  .test-toolbar-content {
    flex-wrap: nowrap !important;
    gap: 6px !important;
    padding: 6px 8px !important;
    min-height: 56px !important;
  }

  /* Mobile Toolbar Left Section */
  .toolbar-left-section {
    gap: 8px;
    min-width: 0;
    flex: 1;
  }

  /* Mobile Toolbar Right Section */
  .toolbar-right-section {
    gap: 6px;
    flex-shrink: 0;
  }

  /* Mobile Top Actions */
  .mobile-top-actions {
    display: flex !important;
    padding: 10px 16px;
  }
  
  /* Mobile Question Indicators */
  .question-indicators {
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    flex-wrap: nowrap;
    scrollbar-width: thin;
    padding: 8px 12px;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    scroll-padding: 0 12px;
  }

  .question-indicator {
    width: 32px;
    height: 32px;
    font-size: 0.75rem;
    flex-shrink: 0;
  }

  .test-interface.fullscreen {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    /* --test-vh = window.innerHeight JS Р°СЂТ›С‹Р»С‹ РѕСЂРЅР°С‚С‹Р»Р°РґС‹ (Р±СЂР°СѓР·РµСЂ toolbar РєРµР№С–РЅРіС– РЅР°Т›С‚С‹ Р±РёС–РєС‚С–Рє) */
    height: var(--test-vh, 100svh) !important;
    z-index: 9999 !important;
    background: #1a1a1a !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    overscroll-behavior-y: contain !important;
    -webkit-overflow-scrolling: touch !important;
    touch-action: pan-y !important;
    scroll-padding-bottom: calc(120px + env(safe-area-inset-bottom, 0px)) !important;
    padding-bottom: env(safe-area-inset-bottom, 0px) !important;
    /* Sticky topbar Р¶Т±РјС‹СЃ Р¶Р°СЃР°СЃС‹РЅ ТЇС€С–РЅ flex column РєРµСЂРµРє */
    display: flex !important;
    flex-direction: column !important;
  }

  .close-test-btn {
    display: none !important; /* Hide floating button on mobile */
  }
  
  .close-test-btn-toolbar {
    margin-left: 6px !important;
    flex-shrink: 0 !important;
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    min-height: 32px !important;
  }
  
  .close-test-btn-toolbar .v-icon {
    font-size: 18px !important;
  }

  .language-btn {
    min-width: 40px !important;
    height: 28px !important;
    padding: 0 6px !important;
    font-size: 0.7rem !important;
    background: rgba(0, 0, 0, 0.1) !important;
    border-color: rgba(255, 255, 255, 0.15) !important;
    color: rgba(255, 255, 255, 0.7) !important;
    backdrop-filter: blur(2px) !important;
  }

  .language-btn:hover {
    background: rgba(255, 193, 7, 0.1) !important;
    border-color: rgba(255, 193, 7, 0.3) !important;
    color: rgba(255, 255, 255, 0.9) !important;
  }

  .timer {
    font-size: 0.85rem !important;
    gap: 4px !important;
  }

  .timer .v-icon {
    font-size: 16px !important;
  }

  /* Mobile: Sticky topbar вЂ” position:fixed Р±Т±Р·С‹Р»Р°РґС‹ iOS-С‚Р°, sticky = scroll container-РіРµ Р¶Р°Р±С‹СЃР°РґС‹ */
  .test-topbar-container {
    position: sticky !important;
    top: 0 !important;
    z-index: 1100 !important;
    flex-shrink: 0 !important;
  }

  .test-interface {
    min-height: 100vh !important;
    min-height: 100dvh !important;
    height: auto !important;
    touch-action: pan-y !important;
    -webkit-overflow-scrolling: touch !important;
  }

  .test-content {
    flex-direction: column !important;
    overflow-y: visible !important;
    overflow-x: hidden !important;
    padding-top: 0 !important; /* Sticky topbar У©Р· РѕСЂРЅС‹РЅ Р°Р»Р°РґС‹ вЂ” padding Т›Р°Р¶РµС‚ РµРјРµСЃ */
    padding-left: 0 !important;
    padding-right: 0 !important;
    padding-bottom: 0 !important;
    height: auto !important;
    min-height: 100vh !important;
    min-height: 100dvh !important;
    max-width: 100vw !important;
    width: 100vw !important;
    display: block !important;
    touch-action: pan-y !important;
    -webkit-overflow-scrolling: touch !important;
  }

  .test-content * {
    box-sizing: border-box !important;
  }

  .test-content .v-container {
    padding: 0 !important;
    margin: 0 !important;
    max-width: 100% !important;
  }

  .test-content .v-row {
    margin: 0 !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .test-content .v-col {
    padding: 0 !important;
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }

  .test-topbar-container {
    position: sticky !important;
    top: 0 !important;
    z-index: 10000 !important;
    flex-shrink: 0 !important;
  }

  .test-toolbar-content {
    padding: 6px 12px !important;
    height: 50px !important;
    font-size: 0.85rem !important;
  }

  .toolbar-title {
    color: #ffffff !important;
  }

  .toolbar-title .text-primary {
    color: #FFC107 !important;
  }

  .toolbar-title .text-medium-emphasis {
    color: rgba(255, 255, 255, 0.7) !important;
  }

  /* Mobile Language Selector */
  .language-selector {
    margin-right: 8px !important;
    width: 70px !important;
  }

  .language-select {
    width: 70px !important;
    min-width: 70px !important;
  }

  .language-select .v-field {
    height: 36px !important;
  }

  .language-select .v-field__input {
    font-size: 11px !important;
    min-height: 36px !important;
    padding: 0 6px !important;
  }

  .language-selected {
    font-size: 11px !important;
  }

  .test-row {
    flex-direction: column !important;
    height: auto !important;
    min-height: 100vh !important;
    overflow: visible !important;
    margin: 0 !important;
    width: 100vw !important;
    max-width: 100vw !important;
    display: flex !important;
  }

  .test-row > .v-col {
    flex: 0 0 100% !important;
    max-width: 100% !important;
    width: 100% !important;
    display: block !important;
  }

  .v-col-12 {
    flex: 0 0 100% !important;
    max-width: 100% !important;
    width: 100% !important;
  }

  .v-col-md-6 {
    flex: 0 0 100% !important;
    max-width: 100% !important;
    width: 100% !important;
  }

  .video-section {
    width: 100% !important;
    max-width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
    background: #000 !important;
    flex-shrink: 0 !important;
    flex-basis: 100% !important;
    order: 1 !important;
    display: block !important;
  }

  .video-container {
    height: auto !important;
    width: 100% !important;
    max-width: 100% !important;
    min-height: 180px !important;
    background: #000 !important;
    overflow: hidden !important;
    display: block !important;
    position: relative !important;
    /* Prevent video from going fullscreen */
    isolation: isolate !important;
  }

  /* Mobile Video Restart Button */
  .video-restart-button {
    position: absolute !important;
    top: 8px !important;
    right: 8px !important;
    z-index: 1000 !important;
    background: rgba(0, 0, 0, 0.8) !important;
    border-radius: 50% !important;
    padding: 4px !important;
  }

  .restart-btn {
    min-width: 40px !important;
    width: 40px !important;
    height: 40px !important;
  }

  .test-video {
    max-height: 30vh !important;
    min-height: 180px !important;
    border-radius: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    object-fit: contain !important;
    display: block !important;
    /* Prevent fullscreen on mobile */
    position: relative !important;
  }
  
  /* Ensure video stays in its container on mobile */
  .test-video:fullscreen {
    display: none !important;
  }
  
  .test-video:-webkit-full-screen {
    display: none !important;
  }
  
  .test-video:-moz-full-screen {
    display: none !important;
  }
  
  .test-video:-ms-fullscreen {
    display: none !important;
  }
  
  /* Hide ALL video controls on mobile */
  .test-video::-webkit-media-controls,
  .test-video::-webkit-media-controls-panel,
  .test-video::-webkit-media-controls-play-button,
  .test-video::-webkit-media-controls-timeline,
  .test-video::-webkit-media-controls-current-time-display,
  .test-video::-webkit-media-controls-time-remaining-display,
  .test-video::-webkit-media-controls-volume-slider,
  .test-video::-webkit-media-controls-mute-button,
  .test-video::-webkit-media-controls-fullscreen-button {
    display: none !important;
  }

  .question-section {
    height: auto !important;
    max-height: none !important;
    padding: 12px !important;
    flex: 0 0 auto !important;
    background: #1a1a1a !important;
    color: #ffffff !important;
    display: block !important;
    overflow-y: visible !important;
    overflow-x: hidden !important;
    min-height: auto !important;
    width: 100% !important;
    max-width: 100% !important;
    order: 2 !important;
  }

  .question-content {
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    display: block !important;
    overflow: visible !important;
  }

  .question-text {
    font-size: 0.95rem !important;
    line-height: 1.4 !important;
    margin-bottom: 12px !important;
    color: #ffffff !important;
    font-weight: 500 !important;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5) !important;
  }

  .answer-options {
    margin: 0 !important;
    padding: 0 !important;
    display: block !important;
    overflow: visible !important;
  }

  .answer-options .v-selection-control-group {
    display: block !important;
  }

  .answer-options .v-radio-group {
    display: block !important;
  }

  /* РЎС‚РёР»Рё РґР»СЏ РІС‹РїР°РґР°СЋС‰РµРіРѕ СЃРїРёСЃРєР° СЏР·С‹РєРѕРІ */
  .language-selector-dropdown {
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
    margin-top: 8px;
  }

  .answer-card {
    margin-bottom: 6px !important;
    padding: 10px 8px !important;
    width: 100% !important;
    max-width: 100% !important;
    border-radius: 8px !important;
    background: #2a2a2a !important;
    border: 2px solid rgba(255, 255, 255, 0.1) !important;
    min-height: 48px !important;
    display: flex !important;
    align-items: center !important;
    box-sizing: border-box !important;
    overflow: visible !important;
    position: relative !important;
    gap: 6px !important; /* Small gap between radio and text */
    transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease !important;
  }

  /* РњРѕР±РёР»СЊРґС– РЅТ±СЃТ›Р°РґР° РњР†РќР”Р•РўРўР† С‚ТЇСЂРґРµ С„РѕРЅРґС‹Т› С‚ТЇСЃС‚РµСЂРґС– Т›РѕР»РґР°РЅСѓ */
  .v-card.answer-card.selected,
  .answer-card.v-card.selected,
  .answer-card.selected {
    border-color: #FFC107 !important;
    background-color: rgba(255, 193, 7, 0.15) !important;
    background: rgba(255, 193, 7, 0.15) !important;
    box-shadow: 0 0 12px rgba(255, 193, 7, 0.3) !important;
  }

  .v-card.answer-card.correct,
  .answer-card.v-card.correct,
  .answer-card.correct {
    border-color: #4CAF50 !important;
    background-color: rgba(76, 175, 80, 0.25) !important;
    background: rgba(76, 175, 80, 0.25) !important;
    box-shadow: 0 0 12px rgba(76, 175, 80, 0.3) !important;
  }

  .v-card.answer-card.incorrect,
  .answer-card.v-card.incorrect,
  .answer-card.incorrect {
    border-color: #F44336 !important;
    background-color: rgba(244, 67, 54, 0.25) !important;
    background: rgba(244, 67, 54, 0.25) !important;
    box-shadow: 0 0 12px rgba(244, 67, 54, 0.3) !important;
  }

  .answer-card .text-body-1 {
    color: #ffffff !important;
    font-size: 0.875rem !important;
    line-height: 1.4 !important;
    font-weight: 400 !important;
    flex: 1 !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    white-space: normal !important;
  }

  .answer-option-indicator {
    width: 22px !important;
    height: 22px !important;
    min-width: 22px !important;
    margin-right: 8px !important;
  }

  .answer-option-dot {
    width: 9px !important;
    height: 9px !important;
  }

  .answer-card .d-flex {
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
    flex-wrap: nowrap !important;
  }

  .answer-card .flex-grow-1 {
    flex-grow: 1 !important;
    min-width: 0 !important;
  }

  .answer-card .v-icon {
    color: #ffffff !important;
  }

  .answer-card .v-icon {
    font-size: 1.25rem !important;
  }

  .question-section .mt-6 {
    margin-top: 12px !important;
    margin-bottom: 12px !important;
    padding-bottom: 12px !important;
  }

  .question-section .v-btn {
    width: 100% !important;
    min-height: 44px !important;
    font-size: 0.9rem !important;
  }

  .v-alert {
    margin-top: 10px !important;
    margin-bottom: 10px !important;
    padding: 10px !important;
    background: rgba(33, 150, 243, 0.1) !important;
    border: 1px solid rgba(33, 150, 243, 0.3) !important;
    color: #ffffff !important;
    width: 100% !important;
    overflow: visible !important;
    word-wrap: break-word !important;
    font-size: 0.85rem !important;
    line-height: 1.4 !important;
  }

  .v-alert .text-subtitle-2 {
    color: #2196F3 !important;
    font-weight: 600 !important;
    margin-bottom: 6px !important;
    font-size: 0.875rem !important;
  }

  .v-alert-text {
    color: #ffffff !important;
    line-height: 1.4 !important;
    font-size: 0.85rem !important;
  }

  /* Custom scrollbar for mobile */
  .question-section::-webkit-scrollbar {
    width: 4px;
  }

  .question-section::-webkit-scrollbar-track {
    background: transparent;
  }

  .question-section::-webkit-scrollbar-thumb {
    background: rgba(255, 193, 7, 0.5);
    border-radius: 4px;
  }

  .question-section::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 193, 7, 0.7);
  }

  /* Mobile Action Buttons - Sticky at bottom */
  .action-buttons-container {
    position: sticky !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    background: linear-gradient(to top, rgba(18, 18, 18, 0.98) 0%, rgba(18, 18, 18, 0.95) 80%, transparent 100%) !important;
    padding: 16px 12px calc(20px + env(safe-area-inset-bottom, 0px)) 12px !important;
    margin-top: 16px !important;
    margin-left: -12px !important;
    margin-right: -12px !important;
    margin-bottom: -12px !important;
    z-index: 10 !important;
    backdrop-filter: blur(8px) !important;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.3) !important;
  }

  .action-buttons-container .v-btn {
    font-size: 1rem !important;
    height: 56px !important;
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.4) !important;
  }

  .action-buttons-container .v-btn--size-large {
    min-height: 56px !important;
  }
}

@media (max-width: 768px) {
  .test-interface.fullscreen {
    overflow-y: auto !important;
    overflow-x: hidden !important;
    padding-bottom: 0 !important;
  }

  .test-topbar-container {
    flex: 0 0 auto !important;
  }

  .test-content {
    flex: 1 0 auto !important;
    min-height: auto !important;
    height: auto !important;
    display: block !important;
    flex-direction: initial !important;
    overflow: visible !important;
  }

  .test-row {
    flex: none !important;
    min-height: auto !important;
    height: auto !important;
    display: block !important;
    flex-direction: initial !important;
    overflow: visible !important;
  }

  .video-section {
    flex: 0 0 auto !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #000 !important;
    border-bottom: 1px solid rgba(255, 193, 7, 0.18) !important;
  }

  .video-container {
    height: clamp(180px, 28vh, 240px) !important;
    min-height: 180px !important;
    max-height: 240px !important;
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    overflow: hidden !important;
    background: #000 !important;
  }

  .test-video {
    width: 100% !important;
    height: 100% !important;
    min-height: 0 !important;
    max-height: none !important;
    object-fit: contain !important;
    background: #000 !important;
  }

  .question-section {
    flex: none !important;
    min-height: auto !important;
    height: auto !important;
    display: block !important;
    overflow: visible !important;
    padding: 14px 12px calc(112px + env(safe-area-inset-bottom, 0px)) !important;
    background: #161616 !important;
  }

  .question-content {
    display: block !important;
    flex-direction: initial !important;
    min-height: auto !important;
    gap: 0 !important;
    padding-bottom: 12px !important;
  }

  .question-text {
    margin-bottom: 0 !important;
    padding: 14px 12px !important;
    border-radius: 14px !important;
    border: 1px solid rgba(255, 193, 7, 0.18) !important;
    background: rgba(255, 255, 255, 0.03) !important;
  }

  .answer-options {
    flex: 0 0 auto !important;
  }

  .action-buttons-container {
    margin-top: auto !important;
    bottom: env(safe-area-inset-bottom, 0px) !important;
  }
}
</style>

<style>
/* Global styles to hide navigation elements when test is active */
body:has(.test-system.test-active) .v-navigation-drawer,
body:has(.test-system.test-active) .v-app-bar,
body:has(.test-system.test-active) .v-footer,
body:has(.test-system.test-active) .v-bottom-navigation {
  display: none !important;
}

body:has(.test-system.test-active),
html:has(.test-system.test-active) {
  overscroll-behavior-y: none !important;
}

/* Ensure test system appears above navigation */
.test-system.test-active {
  z-index: 1050 !important;
}

/* Mobile Floating Controls */
.mobile-floating-controls {
  position: fixed;
  top: 80px;
  left: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1002;
  opacity: 0;
  transform: translateX(-60px);
  transition: all 0.3s ease;
  pointer-events: none;
}

.mobile-floating-controls.visible {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
}

.mobile-language-selector {
  position: relative;
}

.language-btn-floating {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
  backdrop-filter: blur(8px) !important;
}

.language-dropdown-floating {
  background: rgba(20, 20, 20, 0.95) !important;
  backdrop-filter: blur(12px) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.6) !important;
  overflow: hidden !important;
  min-width: 120px !important;
  max-width: 120px !important;
}

.language-item-floating {
  min-height: 40px !important;
  max-height: 40px !important;
  padding: 8px 12px !important;
  cursor: pointer !important;
  color: rgba(255, 255, 255, 0.9) !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-align: center !important;
  transition: all 0.2s ease !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
  background: transparent !important;
}

.language-item-floating:last-child {
  border-bottom: none !important;
}

.language-item-floating:hover {
  background: rgba(33, 150, 243, 0.15) !important;
  color: #2196F3 !important;
}

.language-item-floating.v-list-item--active {
  background: rgba(33, 150, 243, 0.25) !important;
  color: #2196F3 !important;
  font-weight: 700 !important;
}

.language-item-floating .v-list-item-title {
  font-size: 13px !important;
  font-weight: inherit !important;
  text-align: center !important;
}

.timer-floating {
  display: flex;
  justify-content: flex-start;
}

.timer-floating .v-chip {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
  backdrop-filter: blur(8px) !important;
}

.close-btn-floating {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
  backdrop-filter: blur(8px) !important;
}

/* Mobile Close Button - Always Visible */
.close-btn-mobile-fixed {
  position: fixed !important;
  top: 12px !important;
  right: 12px !important;
  z-index: 10002 !important;
  box-shadow: 0 4px 16px rgba(244, 67, 54, 0.4) !important;
  backdrop-filter: blur(8px) !important;
}

/* Desktop toolbar right section */
.toolbar-right-section-desktop {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* Desktop language buttons */
.desktop-lang-buttons {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Answered indicator for exam mode */
.question-indicator.answered {
  background: linear-gradient(135deg, #2196F3, #1976D2) !important;
  color: white !important;
  border-color: #1976D2 !important;
}

/* Animation for compact language dropdown */
@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Online Lessons Styles */
.lesson-category-item {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  margin-bottom: 12px;
}

.lesson-category-item:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
  border-color: rgb(25, 118, 210) !important;
  transform: translateX(4px);
}

.lesson-number {
  font-size: 0.875rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.87);
  min-width: 28px;
  display: inline-flex;
  align-items: center;
}

.lesson-number-white {
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background-color: #1976d2;
  min-width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;
}

.lesson-player-card {
  min-height: 600px;
}

.lesson-video-container {
  background: #000;
}

.lesson-video-wrapper {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  background: #000;
  border-radius: 8px;
}

.lesson-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  object-fit: contain;
  background: #000;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.lesson-video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.lesson-video-wrapper {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.lesson-list {
  max-height: 600px;
  overflow-y: auto;
}

.lesson-list .v-list-item {
  cursor: pointer;
}

.lesson-list .v-list-item:hover {
  background: rgba(0, 0, 0, 0.05);
}

@media (max-width: 768px) {
  .lesson-player-card {
    min-height: auto;
  }

  .lesson-video-container {
    padding: 12px !important;
    background: #000;
  }

  .lesson-video-wrapper {
    border-radius: 12px;
    overflow: hidden;
  }

  .lesson-video-overlay {
    display: none;
  }
}
</style>

